import {
  ActionIcon,
  Button,
  Checkbox,
  Code,
  CopyButton,
  FileInput,
  Group,
  Menu,
  Modal,
  NativeSelect,
  SegmentedControl,
  SimpleGrid,
  Table,
  Text,
  TextInput,
} from "@mantine/core"
import {
  type FormEvent,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { AuthenticatedAppShell } from "../../components/authenticated-app-shell"
import { Link, navigate } from "../../components/link"
import {
  AppCard,
  CardSection,
  DataTableShell,
  EmptyState,
  FormActions,
  MetricCard,
  SectionHeader,
  StatusBadge,
} from "../../components/ui"
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiUpload,
  type CampaignChannelDetailState,
  type CampaignChannelKey,
  type CampaignChannelRecord,
  type CampaignGenerationState,
  type CampaignReadinessAction,
  type CapturedEventSummary,
  type CapturedJourneySummary,
  type ConversionValidationResultStatus,
  type ConversionValidationSummary,
  type CreateAdgiroMeasurementPayload,
  type GeneratedLandingPagesResult,
  type GoogleSearchTermDiagnostic,
  type JobActivityEvent,
  type JobRecord,
  type JobTreeRecord,
  type LocationResolutionRecord,
  type ObservedEventPattern,
  type ProviderAccountRecord,
  type ProviderLocationMatchRecord,
  type QueuedCampaignLaunchResult,
  type QueuedGenerationJobResult,
  type QueuedSegmentGenerationJobsResult,
  type RealtimeTrackingEventsPayload,
  type SearchConsoleSetupPayload,
  type SearchConsoleVerifyPayload,
  type SiteAssetRecord,
  type SiteAssetType,
  type SiteAutomationMode,
  type SiteChannelRollupState,
  type SiteCrawlPageRecord,
  type SiteCrawlState,
  type SiteEventMapping,
  type SiteGrowthAutomationState,
  type SiteGrowthSettings,
  type SiteProviderCandidateRecord,
  type SiteProviderLinksPayload,
  type SiteProviderRole,
  type SiteRecord,
  type SiteSegmentRecord,
  type SiteSeoState,
  type SiteTrackingPayload,
} from "../../lib/api"
import { formatLocalDateTime } from "../../lib/dates"
import { formatAccountType, labelForProvider, providerRoleLabel } from "../../lib/provider-labels"
import { goalLabel } from "../../lib/site-labels"
import { CampaignsPanel } from "./site-campaigns-panel"

type SiteTab = "overview" | "segments" | "setup" | "tracking" | "campaigns" | "seo" | "analytics"
type AssetTab = "overview" | "landing" | "targeting" | "copy" | "media"
type MediaAddMethod = "library" | "upload" | "url"

interface SegmentFormState {
  name: string
}

interface AssetFormState {
  assetType: SiteAssetType
  name: string
  value: string
}

interface MediaUploadFormState {
  name: string
  file: File | null
}

interface GrowthAutomationFormState {
  automationMode: SiteAutomationMode
  dailyBudgetCap: string
  experimentBudgetCap: string
  targetCostPerPrimaryConversion: string
  currency: string
}

interface TrackingMappingFormState {
  patternId: string
  name: string
  conversionEventName: string
  ga4EventName: string
  metaEventName: string
}

const emptySegmentForm: SegmentFormState = { name: "" }
const emptyAssetForm: AssetFormState = {
  assetType: "landing_page",
  name: "",
  value: "",
}
const emptyMediaUploadForm: MediaUploadFormState = { name: "", file: null }
const emptyGrowthAutomationForm: GrowthAutomationFormState = {
  automationMode: "manual",
  dailyBudgetCap: "",
  experimentBudgetCap: "",
  targetCostPerPrimaryConversion: "",
  currency: "USD",
}
const emptyTrackingMappingForm: TrackingMappingFormState = {
  patternId: "",
  name: "",
  conversionEventName: "generate_lead",
  ga4EventName: "generate_lead",
  metaEventName: "Lead",
}
export function SiteDetailPage({ siteId, search }: { siteId: string; search: string }) {
  const initialUrlState = siteDetailUrlState(search)
  const [site, setSite] = useState<SiteRecord | null>(null)
  const [segments, setSegments] = useState<SiteSegmentRecord[]>([])
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    initialUrlState.segmentId
  )
  const [segmentAssets, setSegmentAssets] = useState<SiteAssetRecord[]>([])
  const [mediaLibraryAssets, setMediaLibraryAssets] = useState<SiteAssetRecord[]>([])
  const [providerLinks, setProviderLinks] = useState<SiteProviderLinksPayload>({
    links: [],
    candidates: [],
    measurementParentAccounts: [],
  })
  const [crawlState, setCrawlState] = useState<SiteCrawlState | null>(null)
  const [seoState, setSeoState] = useState<SiteSeoState | null>(null)
  const [tracking, setTracking] = useState<SiteTrackingPayload | null>(null)
  const [campaignGeneration, setCampaignGeneration] = useState<CampaignGenerationState | null>(null)
  const [campaignChannelDetail, setCampaignChannelDetail] =
    useState<CampaignChannelDetailState | null>(null)
  const [targetingGenerationJob, setTargetingGenerationJob] = useState<JobRecord | null>(null)
  const [copyGenerationJob, setCopyGenerationJob] = useState<JobRecord | null>(null)
  const [mediaGenerationJob, setMediaGenerationJob] = useState<JobRecord | null>(null)
  const [segmentGenerationJob, setSegmentGenerationJob] = useState<JobRecord | null>(null)
  const [siteGrowthJob, setSiteGrowthJob] = useState<JobRecord | null>(null)
  const [siteGrowthJobTree, setSiteGrowthJobTree] = useState<JobTreeRecord | null>(null)
  const [campaignGenerationJob, setCampaignGenerationJob] = useState<JobRecord | null>(null)
  const [campaignGenerationJobTree, setCampaignGenerationJobTree] = useState<JobTreeRecord | null>(
    null
  )
  const [trackingRealtimeSummary, setTrackingRealtimeSummary] =
    useState<RealtimeTrackingEventsPayload | null>(null)
  const [segmentForm, setSegmentForm] = useState<SegmentFormState>(emptySegmentForm)
  const [assetForm, setAssetForm] = useState<AssetFormState>(emptyAssetForm)
  const [mediaUploadForm, setMediaUploadForm] = useState<MediaUploadFormState>(emptyMediaUploadForm)
  const [growthAutomationForm, setGrowthAutomationForm] =
    useState<GrowthAutomationFormState>(emptyGrowthAutomationForm)
  const [measurementParentAccountId, setMeasurementParentAccountId] = useState("")
  const [trackingRealtimeMinutes, setTrackingRealtimeMinutes] = useState(30)
  const [trackingMappingForm, setTrackingMappingForm] =
    useState<TrackingMappingFormState>(emptyTrackingMappingForm)
  const [activeTab, setActiveTab] = useState<SiteTab>(initialUrlState.tab)
  const [activeAssetTab, setActiveAssetTab] = useState<AssetTab>(initialUrlState.assetTab)
  const [loadingCrawl, setLoadingCrawl] = useState(false)
  const [recrawlingSite, setRecrawlingSite] = useState(false)
  const [stoppingCrawl, setStoppingCrawl] = useState(false)
  const [stoppingGenerationJob, setStoppingGenerationJob] = useState(false)
  const [retryingGenerationJob, setRetryingGenerationJob] = useState(false)
  const [savingSegment, setSavingSegment] = useState(false)
  const [savingAsset, setSavingAsset] = useState(false)
  const [uploadingMedia, setUploadingMedia] = useState(false)
  const [attachingMediaAssetId, setAttachingMediaAssetId] = useState<string | null>(null)
  const [savingTrackingMapping, setSavingTrackingMapping] = useState(false)
  const [checkingTrackingRealtime, setCheckingTrackingRealtime] = useState(false)
  const [validatingConversion, setValidatingConversion] = useState(false)
  const [conversionValidationModalOpen, setConversionValidationModalOpen] = useState(false)
  const [linkingAccount, setLinkingAccount] = useState(false)
  const [creatingMeasurement, setCreatingMeasurement] = useState(false)
  const [searchConsoleSetup, setSearchConsoleSetup] = useState<SearchConsoleSetupPayload | null>(
    null
  )
  const [searchConsoleModalOpen, setSearchConsoleModalOpen] = useState(false)
  const [linkingSearchConsole, setLinkingSearchConsole] = useState(false)
  const [generatingAssets, setGeneratingAssets] = useState(false)
  const [generatingCampaigns, setGeneratingCampaigns] = useState(false)
  const [savingGrowthAutomation, setSavingGrowthAutomation] = useState(false)
  const [campaignActionId, setCampaignActionId] = useState<string | null>(null)
  const [selectedCampaignChannelId, setSelectedCampaignChannelId] = useState<string | null>(
    initialUrlState.campaignChannelId
  )
  const [resolvingAssetId, setResolvingAssetId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const loadSite = useCallback(
    async (preferredSegmentId?: string | null) => {
      const sites = await apiGet<SiteRecord[]>("/api/sites")
      const currentSite = sites.find((candidate) => candidate.siteId === siteId) ?? null
      setSite(currentSite)
      if (!currentSite) return

      const [loadedSegments, loadedProviderLinks] = await Promise.all([
        apiGet<SiteSegmentRecord[]>(`/api/sites/${siteId}/segments`),
        apiGet<SiteProviderLinksPayload>(`/api/sites/${siteId}/provider-links`),
      ])
      setSegments(loadedSegments)
      setProviderLinks(loadedProviderLinks)
      setSelectedSegmentId((currentSegmentId) => {
        const candidateSegmentId = preferredSegmentId ?? currentSegmentId
        return candidateSegmentId &&
          loadedSegments.some((segment) => segment.segmentId === candidateSegmentId)
          ? candidateSegmentId
          : null
      })
    },
    [siteId]
  )

  const loadCrawlState = useCallback(async () => {
    setCrawlState(await apiGet<SiteCrawlState>(`/api/sites/${siteId}/crawl`))
  }, [siteId])

  const loadSeoState = useCallback(async () => {
    setSeoState(await apiGet<SiteSeoState>(`/api/sites/${siteId}/seo`))
  }, [siteId])

  const loadTracking = useCallback(async () => {
    setTracking(await apiGet<SiteTrackingPayload>(`/api/sites/${siteId}/tracking`))
  }, [siteId])

  const refreshCrawlState = useCallback(async () => {
    setLoadingCrawl(true)
    setError(null)
    try {
      await loadCrawlState()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to load crawl status.")
    } finally {
      setLoadingCrawl(false)
    }
  }, [loadCrawlState])

  const loadSegmentAssets = useCallback(async () => {
    if (!selectedSegmentId) {
      setSegmentAssets([])
      return
    }
    setSegmentAssets(
      await apiGet<SiteAssetRecord[]>(`/api/sites/${siteId}/segments/${selectedSegmentId}/assets`)
    )
  }, [siteId, selectedSegmentId])

  const loadMediaLibrary = useCallback(async () => {
    setMediaLibraryAssets(
      await apiGet<SiteAssetRecord[]>(`/api/sites/${siteId}/assets?assetTypes=image,video`)
    )
  }, [siteId])

  const loadMediaGenerationJob = useCallback(async () => {
    if (!selectedSegmentId) {
      setMediaGenerationJob(null)
      return
    }
    const result = await apiGet<QueuedGenerationJobResult>(
      `/api/sites/${siteId}/segments/${selectedSegmentId}/generate/images`
    )
    setMediaGenerationJob(result.job)
  }, [siteId, selectedSegmentId])

  const loadCopyGenerationJob = useCallback(async () => {
    if (!selectedSegmentId) {
      setCopyGenerationJob(null)
      return
    }
    const result = await apiGet<QueuedGenerationJobResult>(
      `/api/sites/${siteId}/segments/${selectedSegmentId}/generate/copy`
    )
    setCopyGenerationJob(result.job)
  }, [siteId, selectedSegmentId])

  const loadTargetingGenerationJob = useCallback(async () => {
    if (!selectedSegmentId) {
      setTargetingGenerationJob(null)
      return
    }
    const result = await apiGet<QueuedGenerationJobResult>(
      `/api/sites/${siteId}/segments/${selectedSegmentId}/generate/targeting`
    )
    setTargetingGenerationJob(result.job)
  }, [siteId, selectedSegmentId])

  const loadSegmentGenerationJob = useCallback(async () => {
    const result = await apiGet<QueuedGenerationJobResult>(`/api/sites/${siteId}/segments/generate`)
    setSegmentGenerationJob(result.job)
  }, [siteId])

  const loadSiteGrowthAutomationState = useCallback(async () => {
    const result = await apiGet<SiteGrowthAutomationState>(`/api/sites/${siteId}/growth-automation`)
    setSiteGrowthJob(result.latestJob)
    setSiteGrowthJobTree((current) =>
      current && result.latestJob && current.root.jobId === result.latestJob.jobId ? current : null
    )
  }, [siteId])

  const loadCampaignGeneration = useCallback(async () => {
    const result = await apiGet<CampaignGenerationState>(`/api/sites/${siteId}/campaign-generation`)
    setCampaignGeneration(result)
    setCampaignGenerationJob(result.latestJob)
    setCampaignGenerationJobTree((current) =>
      current && result.latestJob && current.root.jobId === result.latestJob.jobId ? current : null
    )
  }, [siteId])

  const loadCampaignChannelDetail = useCallback(async () => {
    if (!selectedCampaignChannelId) {
      setCampaignChannelDetail(null)
      return
    }
    const result = await apiGet<CampaignChannelDetailState>(
      `/api/sites/${siteId}/campaign-channels/${selectedCampaignChannelId}`
    )
    setCampaignChannelDetail(result)
  }, [selectedCampaignChannelId, siteId])

  useEffect(() => {
    const next = siteDetailUrlState(search)
    setSelectedCampaignChannelId(next.campaignChannelId)
  }, [search])

  useEffect(() => {
    if (!site) {
      setGrowthAutomationForm(emptyGrowthAutomationForm)
      return
    }
    setGrowthAutomationForm(growthAutomationFormFromSite(site))
  }, [site])

  useEffect(() => {
    let cancelled = false
    void loadSite().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load site.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadSite])

  useEffect(() => {
    let cancelled = false
    setLoadingCrawl(true)
    void loadCrawlState()
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : "Failed to load crawl status.")
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCrawl(false)
      })
    return () => {
      cancelled = true
    }
  }, [loadCrawlState])

  useEffect(() => {
    let cancelled = false
    void loadTracking().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load tracking setup.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadTracking])

  useEffect(() => {
    const latestJob = crawlState?.latestJob ?? null
    if (!isCrawlActive(latestJob)) return

    const interval = window.setInterval(() => {
      void loadCrawlState().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load crawl status.")
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [crawlState?.latestJob, loadCrawlState])

  useEffect(() => {
    const status = tracking?.conversionValidation?.run.status
    if (activeTab !== "tracking" || (status !== "queued" && status !== "running")) return
    const interval = window.setInterval(() => {
      void loadTracking().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load tracking setup.")
      })
    }, 5000)
    return () => window.clearInterval(interval)
  }, [activeTab, loadTracking, tracking?.conversionValidation?.run.status])

  useEffect(() => {
    if (activeTab !== "seo") return
    void loadSeoState().catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Failed to load SEO diagnostics.")
    })
  }, [activeTab, loadSeoState])

  useEffect(() => {
    let cancelled = false
    void loadSegmentAssets().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load segment assets.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadSegmentAssets])

  useEffect(() => {
    let cancelled = false
    void loadMediaLibrary().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load media library.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadMediaLibrary])

  useEffect(() => {
    let cancelled = false
    void loadMediaGenerationJob().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load generation status.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadMediaGenerationJob])

  useEffect(() => {
    let cancelled = false
    void loadCopyGenerationJob().catch((reason: unknown) => {
      if (!cancelled) {
        setError(
          reason instanceof Error ? reason.message : "Failed to load copy generation status."
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadCopyGenerationJob])

  useEffect(() => {
    let cancelled = false
    void loadTargetingGenerationJob().catch((reason: unknown) => {
      if (!cancelled) {
        setError(
          reason instanceof Error ? reason.message : "Failed to load targeting generation status."
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadTargetingGenerationJob])

  useEffect(() => {
    let cancelled = false
    void loadSegmentGenerationJob().catch((reason: unknown) => {
      if (!cancelled) {
        setError(
          reason instanceof Error ? reason.message : "Failed to load segment generation status."
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadSegmentGenerationJob])

  useEffect(() => {
    if (activeTab !== "campaigns") return
    let cancelled = false
    void loadSiteGrowthAutomationState().catch((reason: unknown) => {
      if (!cancelled) {
        setError(
          reason instanceof Error ? reason.message : "Failed to load site growth job status."
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [activeTab, loadSiteGrowthAutomationState])

  useEffect(() => {
    if (activeTab !== "overview" && activeTab !== "campaigns" && activeTab !== "segments") return
    let cancelled = false
    void loadCampaignGeneration().catch((reason: unknown) => {
      if (!cancelled) {
        setError(
          reason instanceof Error ? reason.message : "Failed to load campaign generation status."
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [activeTab, loadCampaignGeneration])

  // A Meta ads account with exactly one Page needs no decision — select it silently in the
  // background (no spinner/error banner; falls back to the manual Page picker if it fails).
  const autoSelectedMetaPages = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (linkingAccount || !site) return
    const metaAdsLink = providerLinks.links.find(
      (link) => link.role === "ads" && link.provider === "meta"
    )
    if (!metaAdsLink || metaSelectedPageId(metaAdsLink.account.metadata)) return
    const options = metaPageOptions(metaAdsLink.account.metadata)
    if (options.length !== 1 || !options[0]) return
    const linkId = metaAdsLink.siteProviderLinkId
    if (autoSelectedMetaPages.current.has(linkId)) return
    const pageId = options[0].value
    autoSelectedMetaPages.current.add(linkId)
    void (async () => {
      try {
        await apiPost(`/api/sites/${site.siteId}/provider-links/${linkId}/meta-page`, { pageId })
        await loadSite()
      } catch {
        autoSelectedMetaPages.current.delete(linkId)
      }
    })()
  }, [providerLinks.links, linkingAccount, site, loadSite])

  useEffect(() => {
    if (activeTab !== "campaigns" || !selectedCampaignChannelId) {
      setCampaignChannelDetail(null)
      return
    }
    let cancelled = false
    void loadCampaignChannelDetail().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load campaign channel.")
      }
    })
    return () => {
      cancelled = true
    }
  }, [activeTab, loadCampaignChannelDetail, selectedCampaignChannelId])

  useEffect(() => {
    if (!isJobActive(mediaGenerationJob)) return

    const interval = window.setInterval(() => {
      void loadMediaGenerationJob().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load generation status.")
      })
      void loadSegmentAssets().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load segment assets.")
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [mediaGenerationJob, loadMediaGenerationJob, loadSegmentAssets])

  useEffect(() => {
    if (!isJobActive(copyGenerationJob)) return

    const interval = window.setInterval(() => {
      void loadCopyGenerationJob().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load copy generation status."
        )
      })
      void loadSegmentAssets().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load segment assets.")
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [copyGenerationJob, loadCopyGenerationJob, loadSegmentAssets])

  useEffect(() => {
    if (!isJobActive(targetingGenerationJob)) return

    const interval = window.setInterval(() => {
      void loadTargetingGenerationJob().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load targeting generation status."
        )
      })
      void loadSegmentAssets().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load segment assets.")
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [targetingGenerationJob, loadTargetingGenerationJob, loadSegmentAssets])

  useEffect(() => {
    if (!isJobActive(segmentGenerationJob)) return

    const interval = window.setInterval(() => {
      void loadSegmentGenerationJob().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load segment generation status."
        )
      })
      void loadSite().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load segments.")
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [segmentGenerationJob, loadSegmentGenerationJob, loadSite])

  useEffect(() => {
    if (!siteGrowthJob) return
    if (siteGrowthJobTree?.root.jobId === siteGrowthJob.jobId) return

    let cancelled = false
    void apiGet<JobTreeRecord>(`/api/jobs/${siteGrowthJob.jobId}/tree`)
      .then((tree) => {
        if (!cancelled) setSiteGrowthJobTree(tree)
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error ? reason.message : "Failed to load site growth activity."
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [siteGrowthJob, siteGrowthJobTree?.root.jobId])

  useEffect(() => {
    const currentJob = activeJob(siteGrowthJob)
    if (!currentJob) return

    const loadSiteGrowthJobTree = async () => {
      const tree = await apiGet<JobTreeRecord>(`/api/jobs/${currentJob.jobId}/tree`)
      setSiteGrowthJobTree(tree)
      setSiteGrowthJob(tree.root)
    }

    void loadSiteGrowthJobTree().catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Failed to load site growth job status.")
    })

    const interval = window.setInterval(() => {
      void loadSiteGrowthJobTree().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load site growth job status."
        )
      })
      void loadSiteGrowthAutomationState().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load site growth job status."
        )
      })
      void loadCampaignGeneration().catch((reason: unknown) => {
        setError(
          reason instanceof Error ? reason.message : "Failed to load campaign generation status."
        )
      })
    }, 2500)
    return () => window.clearInterval(interval)
  }, [siteGrowthJob, loadCampaignGeneration, loadSiteGrowthAutomationState])

  useEffect(() => {
    const currentJob = activeJob(campaignGenerationJob)
    if (!currentJob) return

    const loadCampaignJobTree = async () => {
      const tree = await apiGet<JobTreeRecord>(`/api/jobs/${currentJob.jobId}/tree`)
      setCampaignGenerationJobTree(tree)
      setCampaignGenerationJob(tree.root)
    }

    if (usesJobTreeStatus(currentJob)) {
      void loadCampaignJobTree().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Failed to load job status.")
      })
    }

    const interval = window.setInterval(() => {
      if (!usesJobTreeStatus(currentJob)) {
        void loadCampaignGeneration().catch((reason: unknown) => {
          setError(
            reason instanceof Error ? reason.message : "Failed to load campaign generation status."
          )
        })
      } else {
        void loadCampaignJobTree().catch((reason: unknown) => {
          setError(reason instanceof Error ? reason.message : "Failed to load job status.")
        })
      }
      if (selectedCampaignChannelId) {
        void loadCampaignChannelDetail().catch((reason: unknown) => {
          setError(reason instanceof Error ? reason.message : "Failed to load campaign channel.")
        })
      }
    }, 2500)
    return () => window.clearInterval(interval)
  }, [
    campaignGenerationJob,
    loadCampaignChannelDetail,
    loadCampaignGeneration,
    selectedCampaignChannelId,
  ])

  useEffect(() => {
    const nextUrlState = siteDetailUrlState(search)
    setActiveTab(nextUrlState.tab)
    setSelectedSegmentId(nextUrlState.segmentId)
    setActiveAssetTab(nextUrlState.assetTab)
  }, [search])

  const title = site?.name ?? "Site"
  const selectedSegment =
    segments.find((segment) => segment.segmentId === selectedSegmentId) ?? null
  const linkedRoles = useMemo(
    () => new Set(providerLinks.links.map((link) => link.role)),
    [providerLinks.links]
  )

  function updateSiteDetailUrl(next: {
    tab?: SiteTab
    segmentId?: string | null
    assetTab?: AssetTab
  }) {
    const nextTab = next.tab ?? activeTab
    const nextSegmentId = next.segmentId === undefined ? selectedSegmentId : next.segmentId
    const nextAssetTab = next.assetTab ?? activeAssetTab
    const params = new URLSearchParams()
    // Overview is the bare default URL. Segments is encoded by its segment deep-link, or an
    // explicit tab param when no segment is selected; every other tab carries an explicit param.
    if (nextTab === "segments") {
      if (nextSegmentId) {
        params.set("segment", nextSegmentId)
      } else {
        params.set("tab", "segments")
      }
      if (nextAssetTab !== "overview") {
        params.set("assetTab", nextAssetTab)
      }
    } else if (nextTab !== "overview") {
      params.set("tab", nextTab)
    }
    const query = params.toString()
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}`
    navigate(nextUrl)
  }

  function selectMainTab(tab: SiteTab) {
    updateSiteDetailUrl({ tab })
  }

  function selectSegment(segmentId: string | null) {
    updateSiteDetailUrl({
      tab: "segments",
      segmentId,
      assetTab: segmentId ? activeAssetTab : "overview",
    })
  }

  function selectAssetTab(assetTab: AssetTab) {
    updateSiteDetailUrl({ assetTab })
  }

  async function createSegment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!site) return

    setSavingSegment(true)
    setError(null)
    try {
      const segment = await apiPost<SiteSegmentRecord>(`/api/sites/${site.siteId}/segments`, {
        name: segmentForm.name,
      })
      setSegmentForm(emptySegmentForm)
      await loadSite(segment.segmentId)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create segment.")
    } finally {
      setSavingSegment(false)
    }
  }

  async function createAsset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!site || !selectedSegmentId) return

    setSavingAsset(true)
    setError(null)
    try {
      await apiPost<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets`,
        assetPayload(assetForm)
      )
      setAssetForm(emptyAssetForm)
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create asset.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function updateAsset(
    assetId: string,
    form: AssetFormState,
    existingAsset?: SiteAssetRecord | null
  ) {
    if (!site || !selectedSegmentId) return

    setSavingAsset(true)
    setError(null)
    try {
      await apiPatch<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}`,
        assetPayload(form, existingAsset)
      )
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to update asset.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function updateImageGroup(assets: SiteAssetRecord[], title: string) {
    if (!site || !selectedSegmentId) return
    const nextTitle = title.trim()
    if (!nextTitle || assets.length === 0) return

    setSavingAsset(true)
    setError(null)
    try {
      await Promise.all(
        assets.map((asset) =>
          apiPatch<SiteAssetRecord>(
            `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${asset.assetId}`,
            imageGroupAssetPayload(asset, nextTitle)
          )
        )
      )
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to update image group.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function deleteAsset(assetId: string) {
    if (!site || !selectedSegmentId) return
    if (!window.confirm("Delete this asset?")) return

    setSavingAsset(true)
    setError(null)
    try {
      await apiDelete(`/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}`)
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to delete asset.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function deleteAssets(assetIds: string[]) {
    if (!site || !selectedSegmentId || assetIds.length === 0) return
    if (
      !window.confirm(`Delete ${String(assetIds.length)} asset${assetIds.length === 1 ? "" : "s"}?`)
    ) {
      return
    }

    setSavingAsset(true)
    setError(null)
    try {
      await Promise.all(
        assetIds.map((assetId) =>
          apiDelete(`/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}`)
        )
      )
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to delete assets.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function resolveAsset(assetId: string) {
    if (!site || !selectedSegmentId) return

    setResolvingAssetId(assetId)
    setError(null)
    try {
      await apiPost<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}/resolve`
      )
      await loadSegmentAssets()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to resolve asset.")
    } finally {
      setResolvingAssetId(null)
    }
  }

  async function dismissProviderLocation(assetId: string, provider: string, locationKey: string) {
    if (!site || !selectedSegmentId) return

    setSavingAsset(true)
    setError(null)
    try {
      await apiPost<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}/resolve/locations/dismiss`,
        { provider, locationKey }
      )
      await loadSegmentAssets()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to dismiss provider match.")
    } finally {
      setSavingAsset(false)
    }
  }

  async function generateTrackingMappings() {
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPost<{ mappings: SiteEventMapping[] }>(
        `/api/sites/${site.siteId}/tracking/mappings/generate`
      )
      await loadTracking()
      setNotice("Tracking mappings generated.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to generate tracking mappings.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function syncTrackingEvents() {
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPost<{ accepted: number }>(`/api/sites/${site.siteId}/tracking/events/sync`, {
        days: 30,
      })
      await loadTracking()
      setNotice("GA4 events synced.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to sync GA4 events.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function checkTrackingRealtime() {
    if (!site) return
    setCheckingTrackingRealtime(true)
    setError(null)
    try {
      const summary = await apiPost<RealtimeTrackingEventsPayload>(
        `/api/sites/${site.siteId}/tracking/events/realtime`,
        { minutes: trackingRealtimeMinutes }
      )
      setTrackingRealtimeSummary(summary)
      setNotice("Realtime GA4 event summary refreshed.")
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Failed to check realtime tracking events."
      )
    } finally {
      setCheckingTrackingRealtime(false)
    }
  }

  async function validateConversionDelivery(scope: "any" | "selected", mappingId?: string) {
    if (!site) return
    if (scope === "selected" && !mappingId) return
    setValidatingConversion(true)
    setError(null)
    try {
      const result = await apiPost<{
        validation: ConversionValidationSummary
        job: JobRecord
      }>(`/api/sites/${site.siteId}/tracking/conversion-validation`, {
        scope,
        mappingId,
        windowMinutes: 30,
      })
      setTracking((current) =>
        current ? { ...current, conversionValidation: result.validation } : current
      )
      setConversionValidationModalOpen(false)
      setNotice("Conversion delivery validation queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to queue validation.")
    } finally {
      setValidatingConversion(false)
    }
  }

  async function createTrackingMapping(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPost<SiteEventMapping>(`/api/sites/${site.siteId}/tracking/mappings`, {
        patternId: trackingMappingForm.patternId || null,
        name: trackingMappingForm.name,
        conversionEventName: trackingMappingForm.conversionEventName,
        ga4EventName: trackingMappingForm.ga4EventName,
        metaEventName: trackingMappingForm.metaEventName,
      })
      setTrackingMappingForm(emptyTrackingMappingForm)
      await loadTracking()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create tracking mapping.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function createTrackingMappingFromSummary(summary: CapturedEventSummary) {
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPost<SiteEventMapping>(`/api/sites/${site.siteId}/tracking/mappings`, {
        patternId: summary.primaryPatternId,
        name: summary.label,
        conversionEventName:
          summary.suggestedConversionEventName ?? defaultConversionEventName(summary.intent),
        ga4EventName: summary.suggestedGa4EventName ?? defaultGa4EventName(summary.intent),
        metaEventName: summary.suggestedMetaEventName ?? defaultMetaEventName(summary.intent),
        rationale: summary.rationale,
      })
      await loadTracking()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create tracking mapping.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function createTrackingMappingFromJourney(summary: CapturedJourneySummary) {
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPost<SiteEventMapping>(`/api/sites/${site.siteId}/tracking/mappings`, {
        patternId: null,
        name: summary.label,
        conversionEventName:
          summary.suggestedConversionEventName ?? defaultConversionEventName(summary.intent),
        ga4EventName: summary.suggestedGa4EventName ?? defaultGa4EventName(summary.intent),
        metaEventName: summary.suggestedMetaEventName ?? defaultMetaEventName(summary.intent),
        rationale: summary.rationale,
        valueJson: journeyMappingValue(summary),
      })
      await loadTracking()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create tracking mapping.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function updateTrackingMappingStatus(
    mappingId: string,
    status: SiteEventMapping["status"]
  ) {
    if (!site) return
    setSavingTrackingMapping(true)
    setError(null)
    try {
      await apiPatch<SiteEventMapping | null>(
        `/api/sites/${site.siteId}/tracking/mappings/${mappingId}`,
        { status }
      )
      await loadTracking()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to update tracking mapping.")
    } finally {
      setSavingTrackingMapping(false)
    }
  }

  async function uploadMedia(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!site || !selectedSegmentId || !mediaUploadForm.file) return

    setUploadingMedia(true)
    setError(null)
    try {
      const body = new FormData()
      body.set("file", mediaUploadForm.file)
      if (mediaUploadForm.name.trim()) body.set("name", mediaUploadForm.name.trim())
      await apiUpload<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/media`,
        body
      )
      setMediaUploadForm(emptyMediaUploadForm)
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to upload media.")
    } finally {
      setUploadingMedia(false)
    }
  }

  async function attachMediaAsset(assetId: string) {
    if (!site || !selectedSegmentId) return

    setAttachingMediaAssetId(assetId)
    setError(null)
    try {
      await apiPost<SiteAssetRecord>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/assets/${assetId}/attach`
      )
      await Promise.all([loadSite(), loadSegmentAssets(), loadMediaLibrary()])
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to add media from library.")
    } finally {
      setAttachingMediaAssetId(null)
    }
  }

  async function linkProviderAccount(role: SiteProviderRole, providerAccountId: string) {
    if (!site || !providerAccountId) return
    setLinkingAccount(true)
    setError(null)
    try {
      await apiPost(`/api/sites/${site.siteId}/provider-links`, { providerAccountId, role })
      await loadSite()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to link provider account.")
    } finally {
      setLinkingAccount(false)
    }
  }

  async function unlinkProviderAccount(siteProviderLinkId: string) {
    if (!site) return
    setLinkingAccount(true)
    setError(null)
    try {
      await apiDelete(`/api/sites/${site.siteId}/provider-links/${siteProviderLinkId}`)
      await loadSite()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to unlink provider account.")
    } finally {
      setLinkingAccount(false)
    }
  }

  async function selectMetaPage(siteProviderLinkId: string, pageId: string) {
    if (!site || !pageId) return
    setLinkingAccount(true)
    setError(null)
    try {
      await apiPost(`/api/sites/${site.siteId}/provider-links/${siteProviderLinkId}/meta-page`, {
        pageId,
      })
      await loadSite()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to select Meta Page.")
    } finally {
      setLinkingAccount(false)
    }
  }

  async function refreshMetaPages(siteProviderLinkId: string) {
    if (!site) return
    setLinkingAccount(true)
    setError(null)
    try {
      await apiPost(
        `/api/sites/${site.siteId}/provider-links/${siteProviderLinkId}/meta-pages/refresh`
      )
      await loadSite()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to refresh Meta Pages.")
    } finally {
      setLinkingAccount(false)
    }
  }

  async function autoLinkProviderAccounts() {
    if (!site) return
    setLinkingAccount(true)
    setError(null)
    try {
      await apiPost(`/api/sites/${site.siteId}/provider-links/auto-link`)
      await loadSite()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to auto-link provider accounts.")
    } finally {
      setLinkingAccount(false)
    }
  }

  async function startSearchConsoleSetup() {
    if (!site) return
    setLinkingSearchConsole(true)
    setError(null)
    try {
      const setup = await apiPost<SearchConsoleSetupPayload>(
        `/api/sites/${site.siteId}/search-console/setup`
      )
      setSearchConsoleSetup(setup)
      setSearchConsoleModalOpen(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to start Search Console setup.")
    } finally {
      setLinkingSearchConsole(false)
    }
  }

  async function verifySearchConsoleSetup() {
    if (!site) return
    setLinkingSearchConsole(true)
    setError(null)
    try {
      const result = await apiPost<SearchConsoleVerifyPayload>(
        `/api/sites/${site.siteId}/search-console/verify`
      )
      if (result.linked) {
        setSearchConsoleModalOpen(false)
        setSearchConsoleSetup(null)
        await loadSite()
        setNotice("Search Console linked.")
      } else if (result.setup) {
        setSearchConsoleSetup(result.setup)
        setNotice("Google has not found the TXT record yet.")
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to verify Search Console.")
    } finally {
      setLinkingSearchConsole(false)
    }
  }

  async function createAdgiroMeasurement() {
    if (!site) return
    const existingAnalyticsLinks = providerLinks.links.filter((link) => link.role === "analytics")
    if (
      existingAnalyticsLinks.length > 0 &&
      !window.confirm(
        "Create a new Adgiro measurement property and replace the linked GA4 property?"
      )
    ) {
      return
    }
    setCreatingMeasurement(true)
    setError(null)
    try {
      await apiPost<CreateAdgiroMeasurementPayload>(
        `/api/sites/${site.siteId}/tracking/measurement/create`,
        measurementParentAccountId ? { providerAccountId: measurementParentAccountId } : {}
      )
      const [loadedProviderLinks, loadedTracking] = await Promise.all([
        apiGet<SiteProviderLinksPayload>(`/api/sites/${site.siteId}/provider-links`),
        apiGet<SiteTrackingPayload>(`/api/sites/${site.siteId}/tracking`),
      ])
      setProviderLinks(loadedProviderLinks)
      setTracking(loadedTracking)
      setNotice("Adgiro measurement property created.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create measurement property.")
    } finally {
      setCreatingMeasurement(false)
    }
  }

  async function recrawlSite() {
    if (!site) return
    if (isCrawlActive(crawlState?.latestJob)) return
    setRecrawlingSite(true)
    setError(null)
    setNotice(null)
    try {
      const crawl = await apiPost<SiteCrawlState>(`/api/sites/${site.siteId}/crawl`)
      setCrawlState(crawl)
      setNotice("Site crawl queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to request a recrawl.")
    } finally {
      setRecrawlingSite(false)
    }
  }

  async function stopCrawl() {
    if (!site || !isCrawlActive(crawlState?.latestJob)) return
    setStoppingCrawl(true)
    setError(null)
    setNotice(null)
    try {
      const crawl = await apiPost<SiteCrawlState>(`/api/sites/${site.siteId}/crawl/stop`)
      setCrawlState(crawl)
      setNotice("Site crawl stop requested.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to stop the crawl.")
    } finally {
      setStoppingCrawl(false)
    }
  }

  async function stopGenerationJob(jobId: string) {
    setStoppingGenerationJob(true)
    setError(null)
    setNotice(null)
    try {
      const job = await apiPost<JobRecord>(`/api/jobs/${jobId}/stop`)
      if (job.jobType === "generate_copy") {
        setCopyGenerationJob(job)
      } else if (job.jobType === "generate_targeting") {
        setTargetingGenerationJob(job)
      } else if (job.jobType === "optimize_site_growth") {
        setSiteGrowthJob(job)
      } else if (job.jobType === "sync_campaign_channels") {
        setCampaignGenerationJob(job)
      } else {
        setMediaGenerationJob(job)
      }
      setNotice(
        job.jobType === "optimize_site_growth"
          ? "Site growth stop requested."
          : "Generation stop requested."
      )
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to stop generation.")
    } finally {
      setStoppingGenerationJob(false)
    }
  }

  async function retryFailedGenerationVariants(jobId: string) {
    if (!site || !selectedSegmentId) return
    setRetryingGenerationJob(true)
    setError(null)
    setNotice(null)
    try {
      const result = await apiPost<QueuedGenerationJobResult>(
        `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/images/${jobId}/retry-failed`
      )
      setMediaGenerationJob(result.job)
      setNotice("Skipped image variants queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to retry skipped variants.")
    } finally {
      setRetryingGenerationJob(false)
    }
  }

  async function generateAssets(scope: string) {
    if (!site) return
    setGeneratingAssets(true)
    setError(null)
    setNotice(null)
    try {
      if (scope === "Segment" || scope === "segments") {
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/generate`
        )
        setSegmentGenerationJob(result.job)
        setNotice("Segment generation queued.")
        return
      }

      if (scope === "landing" && selectedSegmentId) {
        const result = await apiPost<GeneratedLandingPagesResult>(
          `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/landing-pages`
        )
        await Promise.all([loadSite(selectedSegmentId), loadSegmentAssets()])
        setNotice(
          result.assets.length > 0
            ? `Generated ${String(result.assets.length)} landing page asset${result.assets.length === 1 ? "" : "s"} with ${result.model}.`
            : `No new landing page assets were created with ${result.model}.`
        )
        return
      }

      if (scope === "overview" && selectedSegmentId) {
        const result = await apiPost<QueuedSegmentGenerationJobsResult>(
          `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/all`
        )
        setCopyGenerationJob(result.jobs.copy)
        setTargetingGenerationJob(result.jobs.targeting)
        setMediaGenerationJob(result.jobs.media)
        await loadSegmentAssets()
        setNotice(
          result.reusedMediaAssets.length > 0
            ? `Generation queued. Reused ${String(result.reusedMediaAssets.length)} existing media asset${result.reusedMediaAssets.length === 1 ? "" : "s"}.`
            : "Generation queued."
        )
        return
      }

      if ((scope === "media" || scope === "image" || scope === "video") && selectedSegmentId) {
        const mediaTypes =
          scope === "image" || scope === "video" ? { mediaTypes: [scope] } : undefined
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/images`,
          mediaTypes
        )
        setMediaGenerationJob(result.job)
        setNotice(
          scope === "image"
            ? "Image generation queued."
            : scope === "video"
              ? "Video generation queued."
              : "Media generation queued."
        )
        return
      }

      if ((scope === "copy" || isCopyAssetType(scope)) && selectedSegmentId) {
        const assetType = isCopyAssetType(scope) ? scope : null
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/copy`,
          assetType ? { assetTypes: [assetType] } : {}
        )
        setCopyGenerationJob(result.job)
        setNotice(
          assetType
            ? `${generateAssetTypeLabel(assetType)} generation queued.`
            : "Copy generation queued."
        )
        return
      }

      if ((scope === "targeting" || isTargetingAssetType(scope)) && selectedSegmentId) {
        const assetType = isTargetingAssetType(scope) ? scope : null
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${selectedSegmentId}/generate/targeting`,
          assetType ? { assetTypes: [assetType] } : {}
        )
        setTargetingGenerationJob(result.job)
        setNotice(
          assetType
            ? `${generateAssetTypeLabel(assetType)} generation queued.`
            : "Targeting generation queued."
        )
        return
      }

      const crawl = await apiPost<SiteCrawlState>(`/api/sites/${site.siteId}/crawl`)
      setCrawlState(crawl)
      const pageCount = crawl.intelligence?.summary.pageCount
      const imageCount = crawl.intelligence?.summary.imageCount
      setNotice(
        `${scope} generation context is ready from ${String(pageCount ?? crawl.pages.length)} pages and ${String(
          imageCount ?? 0
        )} images. Asset synthesis is next.`
      )
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to prepare generation context.")
    } finally {
      setGeneratingAssets(false)
    }
  }

  async function generateReadinessAssets(
    segmentId: string,
    action: CampaignReadinessAction,
    assetTab: AssetTab
  ) {
    if (!site) return
    setGeneratingAssets(true)
    setError(null)
    setNotice(null)
    updateSiteDetailUrl({ tab: "segments", segmentId, assetTab })
    try {
      if (action.scope === "landing") {
        const result = await apiPost<GeneratedLandingPagesResult>(
          `/api/sites/${site.siteId}/segments/${segmentId}/generate/landing-pages`
        )
        await Promise.all([loadSite(segmentId), loadSegmentAssets()])
        setNotice(
          result.assets.length > 0
            ? `Generated ${String(result.assets.length)} landing page asset${result.assets.length === 1 ? "" : "s"}.`
            : "No new landing page assets were created."
        )
        return
      }
      if (action.scope === "media") {
        const mediaTypes = action.assetTypes.filter(
          (assetType): assetType is "image" | "video" =>
            assetType === "image" || assetType === "video"
        )
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${segmentId}/generate/images`,
          mediaTypes.length > 0 ? { mediaTypes } : {}
        )
        setMediaGenerationJob(result.job)
        setNotice(`${action.label} queued.`)
        return
      }
      if (action.scope === "copy") {
        const assetTypes = action.assetTypes.filter(isCopyAssetType)
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${segmentId}/generate/copy`,
          assetTypes.length > 0
            ? { assetTypes, generationMode: "readiness" }
            : { generationMode: "readiness" }
        )
        setCopyGenerationJob(result.job)
        setNotice(`${action.label} queued.`)
        return
      }
      if (action.scope === "targeting") {
        const assetTypes = action.assetTypes.filter(isTargetingAssetType)
        const result = await apiPost<QueuedGenerationJobResult>(
          `/api/sites/${site.siteId}/segments/${segmentId}/generate/targeting`,
          assetTypes.length > 0
            ? { assetTypes, generationMode: "readiness" }
            : { generationMode: "readiness" }
        )
        setTargetingGenerationJob(result.job)
        setNotice(`${action.label} queued.`)
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `Failed to queue ${action.label}.`)
    } finally {
      setGeneratingAssets(false)
    }
  }

  async function syncCampaignChannels(
    input: {
      campaignChannelIds?: string[]
      targets?: Array<{
        segmentId: string
        channel: CampaignChannelKey
        dailyBudgetAmount?: string
      }>
    } = {}
  ) {
    if (!site) return
    setGeneratingCampaigns(true)
    setError(null)
    setNotice(null)
    try {
      const result = await apiPost<QueuedGenerationJobResult>(
        `/api/sites/${site.siteId}/campaign-generation`,
        input
      )
      if (result.blockedByActiveSync) {
        setCampaignGenerationJob(result.blockedByActiveSync.job)
        await loadCampaignGeneration()
        setNotice(result.blockedByActiveSync.message)
        return
      }
      setCampaignGenerationJob(result.job)
      await loadCampaignGeneration()
      setNotice(input.targets?.length ? "Campaign launch queued." : "Campaign channel sync queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to queue campaign channel sync.")
    } finally {
      setGeneratingCampaigns(false)
    }
  }

  async function syncCampaignChannel(campaignChannelId: string) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      const result = await apiPost<QueuedCampaignLaunchResult>(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/sync`
      )
      if (result.blockedByActiveSync) {
        setCampaignGenerationJob(result.blockedByActiveSync.job)
        await loadCampaignGeneration()
        await loadCampaignChannelDetail()
        setNotice(result.blockedByActiveSync.message)
        return
      }
      setCampaignGenerationJob(result.job)
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Campaign channel sync queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to queue campaign channel sync.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function archiveCampaignChannel(campaignChannelId: string) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPost(`/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/archive`)
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Campaign channel archived.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to archive campaign channel.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function unarchiveCampaignChannel(campaignChannelId: string) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPost(`/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/unarchive`)
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Campaign channel unarchived.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to unarchive campaign channel.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function deleteCampaignChannelProviderCampaign(campaignChannelId: string) {
    if (!site) return
    if (!window.confirm("Delete this provider campaign and clear its local receipts?")) {
      return
    }
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiDelete(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/provider-campaign`
      )
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Provider campaign deleted.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to delete provider campaign.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function updateCampaignChannelBudget(campaignChannelId: string, budgetAmount: string) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPatch(`/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}`, {
        budgetAmount,
      })
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Campaign channel budget saved.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to save campaign channel budget.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function updateCampaignChannelPlannerInstructions(
    campaignChannelId: string,
    plannerInstructions: string
  ) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPatch(`/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}`, {
        plannerInstructions,
      })
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Campaign planner instructions saved.")
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Failed to save campaign planner instructions."
      )
    } finally {
      setCampaignActionId(null)
    }
  }

  async function updateCampaignChannelServingStatus(
    campaignChannelId: string,
    status: "active" | "paused"
  ) {
    if (!site) return
    if (
      status === "active" &&
      !window.confirm("Resume this provider campaign? Ads may start serving immediately.")
    ) {
      return
    }
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPost(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/serving-status`,
        { status }
      )
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice(status === "active" ? "Campaign resumed." : "Campaign paused.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to update campaign status.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function syncCampaignChannelProviderState(campaignChannelId: string) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      const result = await apiPost<{ channel: CampaignChannelRecord; job: JobRecord }>(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/provider-state`
      )
      setCampaignGenerationJob(result.job)
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice("Provider stats pull queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to queue provider stats pull.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function optimizeCampaignChannel(campaignChannelId: string, dryRun = false) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      const result = await apiPost<{ channel: CampaignChannelRecord; job: JobRecord }>(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/optimize`,
        dryRun ? { dryRun: true } : undefined
      )
      setCampaignGenerationJob(result.job)
      await loadCampaignGeneration()
      await loadCampaignChannelDetail()
      setNotice(dryRun ? "Campaign recommendations queued." : "Campaign optimization queued.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to queue campaign optimization.")
    } finally {
      setCampaignActionId(null)
    }
  }

  async function updateGrowthAutomation(event: FormEvent) {
    event.preventDefault()
    if (!site) return
    setSavingGrowthAutomation(true)
    setError(null)
    setNotice(null)
    try {
      const updated = await apiPatch<SiteRecord>(
        `/api/sites/${site.siteId}/growth-automation`,
        growthAutomationRequest(growthAutomationForm)
      )
      setSite(updated)
      await loadSiteGrowthAutomationState()
      setNotice(
        updated.automationMode === "full_auto"
          ? "Full auto enabled for this site."
          : "Automation settings saved."
      )
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to save automation settings.")
    } finally {
      setSavingGrowthAutomation(false)
    }
  }

  async function addGoogleSearchNegative(
    campaignChannelId: string,
    term: GoogleSearchTermDiagnostic
  ) {
    if (!site) return
    setCampaignActionId(campaignChannelId)
    setError(null)
    setNotice(null)
    try {
      await apiPost(
        `/api/sites/${site.siteId}/campaign-channels/${campaignChannelId}/negative-keywords`,
        { text: term.searchTerm, matchType: "EXACT" }
      )
      await loadCampaignChannelDetail()
      setNotice("Negative keyword added.")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to add negative keyword.")
    } finally {
      setCampaignActionId(null)
    }
  }

  return (
    <AuthenticatedAppShell eyebrow="Site" title={title}>
      {() => (
        <>
          <div className="page-toolbar">
            <p>
              {site?.primaryDomain ?? "Loading site"} · {site ? goalLabel(site.primaryGoal) : ""}
            </p>
            <Link href="/app/sites" className="button button--ghost">
              Back to sites
            </Link>
          </div>

          {error ? (
            <div className="dashboard-card">
              <h2>{site ? "Request failed" : "Site unavailable"}</h2>
              <p>{error}</p>
            </div>
          ) : null}

          {notice ? (
            <div className="dashboard-card dashboard-card--notice">
              <p>{notice}</p>
            </div>
          ) : null}

          {!site && !error ? (
            <section className="dashboard-card">
              <h2>Loading site</h2>
            </section>
          ) : null}

          {site ? (
            <>
              <nav className="tab-list" aria-label="Site sections">
                {siteTabs.map((tab) => (
                  <button
                    className={
                      activeTab === tab.id ? "tab-button tab-button--active" : "tab-button"
                    }
                    key={tab.id}
                    type="button"
                    onClick={() => selectMainTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              {activeTab === "overview" ? (
                <SiteCockpitPanel
                  site={site}
                  generation={campaignGeneration}
                  loading={campaignGeneration === null}
                  providerLinks={providerLinks}
                  tracking={tracking}
                  crawlState={crawlState}
                  recrawling={recrawlingSite}
                  onOpenTab={selectMainTab}
                  onConnectProviders={() => navigate("/app/accounts")}
                  onRecrawl={() => void recrawlSite()}
                />
              ) : null}

              {activeTab === "segments" ? (
                <SegmentsPanel
                  assetForm={assetForm}
                  activeAssetTab={activeAssetTab}
                  assets={segmentAssets}
                  mediaLibraryAssets={mediaLibraryAssets}
                  mediaUploadForm={mediaUploadForm}
                  savingAsset={savingAsset}
                  savingSegment={savingSegment}
                  attachingMediaAssetId={attachingMediaAssetId}
                  uploadingMedia={uploadingMedia}
                  generatingAssets={generatingAssets}
                  segmentGenerationJob={segmentGenerationJob}
                  copyGenerationJob={copyGenerationJob}
                  generationJob={mediaGenerationJob}
                  targetingGenerationJob={targetingGenerationJob}
                  stoppingGeneration={stoppingGenerationJob}
                  campaignGeneration={campaignGeneration}
                  segmentForm={segmentForm}
                  segments={segments}
                  selectedSegment={selectedSegment}
                  onCreateAsset={(event) => void createAsset(event)}
                  onCreateSegment={(event) => void createSegment(event)}
                  onAttachMediaAsset={(assetId) => void attachMediaAsset(assetId)}
                  onDeleteAsset={(assetId) => void deleteAsset(assetId)}
                  onDeleteAssets={(assetIds) => void deleteAssets(assetIds)}
                  onGenerate={(scope) => void generateAssets(scope)}
                  onResolveAsset={(assetId) => void resolveAsset(assetId)}
                  onDismissProviderLocation={(assetId, provider, locationKey) =>
                    void dismissProviderLocation(assetId, provider, locationKey)
                  }
                  onRetryFailedGeneration={(jobId) => void retryFailedGenerationVariants(jobId)}
                  onSelectAssetTab={selectAssetTab}
                  onSelectSegment={selectSegment}
                  onStopGeneration={(jobId) => void stopGenerationJob(jobId)}
                  onUpdateAsset={(assetId, form, asset) => void updateAsset(assetId, form, asset)}
                  onUpdateImageGroup={(assets, title) => void updateImageGroup(assets, title)}
                  onUploadMedia={(event) => void uploadMedia(event)}
                  onUpdateAssetForm={setAssetForm}
                  onUpdateMediaUploadForm={setMediaUploadForm}
                  onUpdateSegmentForm={setSegmentForm}
                  retryingGeneration={retryingGenerationJob}
                  resolvingAssetId={resolvingAssetId}
                />
              ) : null}

              {activeTab === "setup" ? (
                <SiteSetupPanel
                  crawlState={crawlState}
                  linkedRoles={linkedRoles}
                  linkingAccount={linkingAccount}
                  linkingSearchConsole={linkingSearchConsole}
                  loadingCrawl={loadingCrawl}
                  providerLinks={providerLinks}
                  searchConsoleSetup={searchConsoleSetup}
                  searchConsoleModalOpen={searchConsoleModalOpen}
                  selectedMeasurementParentAccountId={measurementParentAccountId}
                  tracking={tracking}
                  creatingMeasurement={creatingMeasurement}
                  recrawlingSite={recrawlingSite}
                  stoppingCrawl={stoppingCrawl}
                  onAutoLink={() => void autoLinkProviderAccounts()}
                  onCreateMeasurement={() => void createAdgiroMeasurement()}
                  onLink={(role, providerAccountId) =>
                    void linkProviderAccount(role, providerAccountId)
                  }
                  onRefreshCrawl={() => void refreshCrawlState()}
                  onRecrawl={() => void recrawlSite()}
                  onCloseSearchConsoleSetup={() => setSearchConsoleModalOpen(false)}
                  onStartSearchConsoleSetup={() => void startSearchConsoleSetup()}
                  onSelectMeasurementParentAccount={setMeasurementParentAccountId}
                  onSelectMetaPage={(siteProviderLinkId, pageId) =>
                    void selectMetaPage(siteProviderLinkId, pageId)
                  }
                  onRefreshMetaPages={(siteProviderLinkId) =>
                    void refreshMetaPages(siteProviderLinkId)
                  }
                  onStopCrawl={() => void stopCrawl()}
                  onUnlink={(siteProviderLinkId) => void unlinkProviderAccount(siteProviderLinkId)}
                  onVerifySearchConsoleSetup={() => void verifySearchConsoleSetup()}
                />
              ) : null}

              {activeTab === "tracking" ? (
                <section className="dashboard-card">
                  <TrackingPanel
                    form={trackingMappingForm}
                    saving={savingTrackingMapping}
                    checkingRealtime={checkingTrackingRealtime}
                    realtimeMinutes={trackingRealtimeMinutes}
                    realtimeSummary={trackingRealtimeSummary}
                    tracking={tracking}
                    validatingConversion={validatingConversion}
                    validationModalOpen={conversionValidationModalOpen}
                    onCheckRealtime={() => void checkTrackingRealtime()}
                    onCloseValidationModal={() => setConversionValidationModalOpen(false)}
                    onCreateMapping={(event) => void createTrackingMapping(event)}
                    onGenerateMappings={() => void generateTrackingMappings()}
                    onOpenValidationModal={() => setConversionValidationModalOpen(true)}
                    onOpenSetup={() => selectMainTab("setup")}
                    onSelectRealtimeMinutes={setTrackingRealtimeMinutes}
                    onSyncEvents={() => void syncTrackingEvents()}
                    onUpdateForm={setTrackingMappingForm}
                    onUpdateStatus={(mappingId, status) =>
                      void updateTrackingMappingStatus(mappingId, status)
                    }
                    onValidateAnyConversion={() => void validateConversionDelivery("any")}
                    onValidateConversion={(mappingId) =>
                      void validateConversionDelivery("selected", mappingId)
                    }
                    onCreateMappingFromSummary={(summary) =>
                      void createTrackingMappingFromSummary(summary)
                    }
                    onCreateMappingFromJourney={(summary) =>
                      void createTrackingMappingFromJourney(summary)
                    }
                  />
                </section>
              ) : null}

              {activeTab === "campaigns" ? (
                <>
                  <GrowthAutomationPanel
                    form={growthAutomationForm}
                    activityTree={
                      siteGrowthJobTree?.root.jobId === siteGrowthJob?.jobId
                        ? siteGrowthJobTree
                        : null
                    }
                    jobStatusPanel={
                      siteGrowthJob ? (
                        <QueuedJobStatusPanel
                          job={siteGrowthJob}
                          tree={
                            siteGrowthJobTree?.root.jobId === siteGrowthJob.jobId
                              ? siteGrowthJobTree
                              : null
                          }
                          retrying={false}
                          stopping={stoppingGenerationJob}
                          onRetryFailed={() => undefined}
                          onStop={() => stopGenerationJob(siteGrowthJob.jobId)}
                        />
                      ) : null
                    }
                    saving={savingGrowthAutomation}
                    onSubmit={(event) => void updateGrowthAutomation(event)}
                    onUpdate={setGrowthAutomationForm}
                  />
                  <CampaignsPanel
                    detail={campaignChannelDetail}
                    detailRoute={Boolean(selectedCampaignChannelId)}
                    generation={campaignGeneration}
                    generating={generatingCampaigns}
                    jobStatusPanel={
                      campaignGenerationJob ? (
                        <QueuedJobStatusPanel
                          job={campaignGenerationJob}
                          tree={
                            campaignGenerationJobTree?.root.jobId === campaignGenerationJob.jobId
                              ? campaignGenerationJobTree
                              : null
                          }
                          retrying={false}
                          stopping={stoppingGenerationJob}
                          onRetryFailed={() => undefined}
                          onStop={() => stopGenerationJob(campaignGenerationJob.jobId)}
                        />
                      ) : null
                    }
                    actionCampaignId={campaignActionId}
                    onArchive={(campaignChannelId) =>
                      void archiveCampaignChannel(campaignChannelId)
                    }
                    onGenerate={(input) => void syncCampaignChannels(input)}
                    onGenerateAssets={(segmentId, action, assetTab) =>
                      void generateReadinessAssets(segmentId, action, assetTab)
                    }
                    onOpenSegment={selectSegment}
                    onLaunch={(campaignChannelId) => void syncCampaignChannel(campaignChannelId)}
                    onOpenAssets={() => selectMainTab("segments")}
                    onDeleteProviderCampaign={(campaignChannelId) =>
                      void deleteCampaignChannelProviderCampaign(campaignChannelId)
                    }
                    onSaveBudget={(campaignChannelId, budgetAmount) =>
                      void updateCampaignChannelBudget(campaignChannelId, budgetAmount)
                    }
                    onSavePlannerInstructions={(campaignChannelId, plannerInstructions) =>
                      void updateCampaignChannelPlannerInstructions(
                        campaignChannelId,
                        plannerInstructions
                      )
                    }
                    onSetServingStatus={(campaignChannelId, status) =>
                      void updateCampaignChannelServingStatus(campaignChannelId, status)
                    }
                    onPullProviderState={(campaignChannelId) =>
                      void syncCampaignChannelProviderState(campaignChannelId)
                    }
                    onAddGoogleSearchNegative={(campaignChannelId, term) =>
                      void addGoogleSearchNegative(campaignChannelId, term)
                    }
                    onOptimize={(campaignChannelId, dryRun) =>
                      void optimizeCampaignChannel(campaignChannelId, dryRun)
                    }
                    onCloseDetail={() =>
                      navigate(`/app/sites/${encodeURIComponent(site.siteId)}?tab=campaigns`)
                    }
                    onUnarchive={(campaignChannelId) =>
                      void unarchiveCampaignChannel(campaignChannelId)
                    }
                  />
                </>
              ) : null}

              {activeTab === "seo" ? (
                <SeoPanel
                  crawlState={crawlState}
                  loadingCrawl={loadingCrawl}
                  linkedSearchConsole={linkedRoles.has("search_console")}
                  seoState={seoState}
                  onOpenSetup={() => selectMainTab("setup")}
                  onRecrawl={() => void recrawlSite()}
                />
              ) : null}

              {activeTab === "analytics" ? (
                <PlaceholderPanel
                  title="Analytics"
                  status="Link GA4 and Search Console to unlock site measurement setup."
                />
              ) : null}
            </>
          ) : null}
        </>
      )}
    </AuthenticatedAppShell>
  )
}

function summaryNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function summaryString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null
}

function latestIndexedAt(crawlState: SiteCrawlState | null): string | null {
  if (!crawlState || crawlState.pages.length === 0) return null
  return crawlState.pages
    .map((page) => page.indexedAt)
    .sort((left, right) => right.localeCompare(left))[0]
}

const activeJobStatuses = new Set(["queued", "running", "stopping"])

function isCrawlActive(job: JobRecord | null | undefined): boolean {
  return isJobActive(job)
}

function isJobActive(job: JobRecord | null | undefined): boolean {
  return Boolean(job && activeJobStatuses.has(job.status))
}

function activeJob(job: JobRecord | null | undefined): JobRecord | null {
  return job && activeJobStatuses.has(job.status) ? job : null
}

function usesJobTreeStatus(job: JobRecord): boolean {
  return job.jobType !== "sync_campaign_channels" || isSingleCampaignChannelSyncJob(job)
}

function isSingleCampaignChannelSyncJob(job: JobRecord): boolean {
  const campaignChannelIds = job.payload?.campaignChannelIds
  return (
    job.jobType === "sync_campaign_channels" &&
    Array.isArray(campaignChannelIds) &&
    campaignChannelIds.length === 1
  )
}

function queuedJobStatusMessage(job: JobRecord): string {
  const resultMessage = summaryString(job.result?.userMessage)
  if (resultMessage && (job.status === "succeeded" || job.status === "failed")) {
    return resultMessage
  }
  if (job.status === "queued") return `${jobActionLabel(job)} is queued and will start shortly.`
  if (job.status === "stopping") {
    return job.jobType === "generate_media"
      ? "Generation is stopping after the current media item finishes."
      : `${jobActionLabel(job)} is stopping.`
  }
  if (job.status === "stopped") return `${jobActionLabel(job)} was stopped.`
  if (job.status === "failed")
    return job.lastError
      ? `${jobActionLabel(job)} failed: ${job.lastError}`
      : `${jobActionLabel(job)} failed.`
  if (job.status === "succeeded") return `${jobActionLabel(job)} finished.`

  const result = job.result ?? {}
  const completed = summaryNumber(result.completed)
  const failed = summaryNumber(result.failed)
  const skipped = summaryNumber(result.skipped)
  const total = summaryNumber(result.total)
  if (total && (completed !== null || failed !== null)) {
    if (job.jobType === "generate_copy") {
      return `Generating copy: ${String(completed ?? 0)} created, ${String(skipped ?? 0)} skipped, ${String(total)} total.`
    }
    return `Rendering media: ${String(completed ?? 0)} complete, ${String(failed ?? 0)} skipped, ${String(total)} total.`
  }
  if (job.jobType === "generate_copy") return "Generating copy."
  if (job.jobType === "optimize_site_growth") return "Running site growth."
  if (job.jobType === "optimize_campaign_channel") return "Optimizing campaign."
  if (job.jobType === "sync_campaign_provider_state") return "Pulling provider stats."
  if (job.jobType === "sync_campaign_channels") return "Syncing campaign channels."
  if (typeof result.stage === "string" && result.stage === "rendering") {
    return "Rendering media."
  }
  return `${jobActionLabel(job)} is running.`
}

function queuedJobTreeStatusMessage(tree: JobTreeRecord | null, job: JobRecord): string {
  if (!tree) return queuedJobStatusMessage(job)
  if (tree.latestError && tree.rollupStatus === "failed") {
    return `${jobActionLabel(tree.root)} failed: ${tree.latestError}`
  }
  if (tree.summary.total > 0 && tree.summary.active > 0) {
    return `${jobActionLabel(tree.root)} is running: ${String(tree.summary.succeeded)} finished, ${String(tree.summary.failed)} failed, ${String(tree.summary.active)} active.`
  }
  if (tree.summary.total > 0 && tree.summary.failed > 0) {
    return `${jobActionLabel(tree.root)} finished with ${String(tree.summary.failed)} failed child job${tree.summary.failed === 1 ? "" : "s"}.`
  }
  return queuedJobStatusMessage(tree.root)
}

function jobActionLabel(job: JobRecord): string {
  if (job.jobType === "optimize_site_growth") return "Site growth"
  if (job.jobType === "optimize_campaign_channel") return "Optimization"
  if (job.jobType === "sync_campaign_provider_state") return "Provider stats pull"
  if (job.jobType === "sync_campaign_channels") return "Campaign sync"
  if (job.jobType === "site_crawl") return "Crawl"
  return "Generation"
}

function jobProgress(job: JobRecord): { completed: number; failed: number; total: number } | null {
  const result = job.result ?? {}
  const total = summaryNumber(result.total)
  if (!total) return null
  const skipped = summaryNumber(result.skipped)
  return {
    completed: summaryNumber(result.completed) ?? 0,
    failed: skipped ?? summaryNumber(result.failed) ?? 0,
    total,
  }
}

interface FullAutoActivityItem {
  key: string
  at: string
  level: "info" | "warn" | "error"
  title: string
  message: string
  details: Record<string, unknown> | null
}

function fullAutoActivityItems(tree: JobTreeRecord | null): FullAutoActivityItem[] {
  if (!tree) return []
  const jobsById = new Map([tree.root, ...tree.children].map((job) => [job.jobId, job]))
  const eventItems = tree.activityEvents.map((event) => {
    const job = event.jobId ? jobsById.get(event.jobId) : null
    return {
      key: `event:${event.opsEventId}`,
      at: event.createdAt,
      level: event.level,
      title: activityEventTitle(event, job ?? null),
      message: event.message,
      details: event.metadata,
    }
  })
  return eventItems
    .sort((left, right) => left.at.localeCompare(right.at) || left.key.localeCompare(right.key))
    .filter((item, index, items) => {
      const previous = items[index - 1]
      return !previous || previous.title !== item.title || previous.message !== item.message
    })
    .slice(-40)
}

function activityEventTitle(event: JobActivityEvent, job: JobRecord | null): string {
  if (event.eventType.startsWith("agent_action_")) {
    const title = event.metadata?.title
    return typeof title === "string" && title.length > 0 ? title : "Decision recorded"
  }
  if (event.eventType === "site_growth_optimization_started") return "Started full auto"
  if (event.eventType === "site_growth_asset_preparation_queued") return "Queued missing assets"
  if (event.eventType === "campaign_channel_sync_started") return "Started campaign sync"
  if (event.eventType.includes("theme_selection")) return "Planned search themes"
  if (event.eventType.includes("theme_build")) return "Built search ad group"
  if (event.eventType.includes("plan_compiled")) return "Compiled campaign plan"
  if (event.eventType.includes("optimization_ai_completed")) return "Selected optimization actions"
  if (event.eventType.includes("optimization_ai_no_actions")) return "No AI actions selected"
  if (event.eventType.includes("provider_state")) return "Checked provider stats"
  if (job) return fullAutoJobDecisionTitle(job)
  return statusLabel(event.eventType)
}

function fullAutoJobDecisionTitle(job: JobRecord): string {
  if (job.jobType === "generate_segments") return "Generate segments"
  if (job.jobType === "generate_landing_pages") return "Generate landing pages"
  if (job.jobType === "generate_targeting") return "Generate targeting"
  if (job.jobType === "generate_media") return "Generate media"
  if (job.jobType === "sync_campaign_channels") return "Sync campaign channels"
  if (job.jobType === "sync_campaign_provider_state") return "Pull provider stats"
  if (job.jobType === "optimize_campaign_channel") return "Optimize campaign channel"
  if (job.jobType === "generate_copy") {
    const assetTypes = Array.isArray(job.payload?.assetTypes)
      ? job.payload.assetTypes.filter((value): value is string => typeof value === "string")
      : []
    if (assetTypes.includes("headline") && assetTypes.includes("description")) {
      return "Generate headlines and descriptions"
    }
    if (assetTypes.length > 0) return `Generate ${assetTypes.map(statusLabel).join(", ")}`
    return "Generate copy"
  }
  return jobActionLabel(job)
}

function FullAutoActivityFeed({ tree }: { tree: JobTreeRecord | null }) {
  const [expanded, setExpanded] = useState<string[]>([])
  const items = fullAutoActivityItems(tree)
  if (!tree) {
    return (
      <div className="full-auto-activity">
        <SectionHeader title="Full auto activity" description="No full-auto run has been loaded." />
      </div>
    )
  }
  return (
    <div className="full-auto-activity">
      <SectionHeader
        title="Full auto activity"
        description={`${String(items.length)} recent activity item${items.length === 1 ? "" : "s"} from the latest run.`}
      />
      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          No activity has been recorded for this run yet.
        </Text>
      ) : (
        <ol className="full-auto-activity__list">
          {items.map((item) => {
            const details = item.details ? compactActivityDetails(item.details) : null
            const isExpanded = expanded.includes(item.key)
            return (
              <li
                className={`full-auto-activity__item full-auto-activity__item--${item.level}`}
                key={item.key}
              >
                <div>
                  <span>{formatDateTime(item.at)}</span>
                  <strong>{item.title}</strong>
                  <small>{item.message}</small>
                  {details && isExpanded ? <pre>{details}</pre> : null}
                </div>
                {details ? (
                  <button
                    className="button button--ghost button--table"
                    type="button"
                    onClick={() =>
                      setExpanded((current) =>
                        current.includes(item.key)
                          ? current.filter((key) => key !== item.key)
                          : [...current, item.key]
                      )
                    }
                  >
                    {isExpanded ? "Hide" : "Details"}
                  </button>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

function compactActivityDetails(value: Record<string, unknown>): string | null {
  const entries = Object.entries(value).filter(([, item]) => item !== null && item !== undefined)
  if (entries.length === 0) return null
  return JSON.stringify(Object.fromEntries(entries.slice(0, 12)), null, 2)
}

function hasRetryableImageFailures(job: JobRecord): boolean {
  const failures = Array.isArray(job.result?.failures) ? job.result.failures : []
  return failures.some((failure) => {
    if (!failure || typeof failure !== "object") return false
    const variant = (failure as { variant?: unknown }).variant
    return variant === "square" || variant === "vertical" || variant === "horizontal"
  })
}

function crawlStatusMessage(job: JobRecord | null, loading: boolean): string {
  if (loading) return "Checking crawl status."
  if (!job) return "No crawl job has been requested yet."
  if (job.status === "queued") return "Crawl is queued and will start shortly."
  if (job.status === "stopping") return "Crawl is stopping after the current chunk finishes."
  if (job.status === "stopped") return "Crawl was stopped before it finished."
  if (job.status === "failed")
    return job.lastError ? `Crawl failed: ${job.lastError}` : "Crawl failed."
  if (job.status === "succeeded") return "Crawl finished."
  if (job.status !== "running") return statusLabel(job.status)

  const payload = job.payload ?? {}
  const result = job.result ?? {}
  const nextCandidateIndex = summaryNumber(payload.nextCandidateIndex)
  const totalCandidates = summaryNumber(result.totalCandidates)
  const imagePageUrl = typeof payload.imagePageUrl === "string" ? payload.imagePageUrl : null
  const nextImageIndex = summaryNumber(payload.nextImageIndex)

  if (imagePageUrl) {
    const label = pathFromUrl(imagePageUrl)
    return nextImageIndex === null
      ? `Caching images from ${label}.`
      : `Caching images from ${label}, starting at image ${String(nextImageIndex + 1)}.`
  }
  if (nextCandidateIndex !== null && totalCandidates !== null && totalCandidates > 0) {
    return `Crawling page ${String(Math.min(nextCandidateIndex + 1, totalCandidates))} of ${String(totalCandidates)}.`
  }
  return "Crawl is running."
}

function pathFromUrl(value: string): string {
  try {
    const url = new URL(value)
    return url.pathname === "/" ? "/" : url.pathname
  } catch {
    return value
  }
}

function formatDateTime(value: string | null): string {
  return formatLocalDateTime(value)
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatPosition(value: number): string {
  return value > 0 ? value.toFixed(1) : "-"
}

function statusLabel(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function CrawlStatusPanel({
  crawlState,
  loading,
  recrawling,
  stopping,
  onRefresh,
  onRecrawl,
  onStop,
}: {
  crawlState: SiteCrawlState | null
  loading: boolean
  recrawling: boolean
  stopping: boolean
  onRefresh: () => void
  onRecrawl: () => void
  onStop: () => void
}) {
  const intelligence = crawlState?.intelligence ?? null
  const pageCount = summaryNumber(intelligence?.summary.pageCount) ?? crawlState?.pages.length ?? 0
  const imageCount = summaryNumber(intelligence?.summary.imageCount) ?? 0
  const failedPages = crawlState?.pages.filter((page) => page.status === "failed").length ?? 0
  const lastGenerated = intelligence?.generatedAt ?? latestIndexedAt(crawlState)
  const latestJob = crawlState?.latestJob ?? null
  const crawlActive = isCrawlActive(latestJob)
  const crawlStopping = latestJob?.status === "stopping"
  const status = loading
    ? "Loading"
    : latestJob && activeJobStatuses.has(latestJob.status)
      ? statusLabel(latestJob.status)
      : intelligence?.status
        ? statusLabel(intelligence.status)
        : "Not crawled"
  const message = crawlStatusMessage(latestJob, recrawling || loading)

  return (
    <AppCard className="crawl-status-card">
      <SectionHeader
        title="Site crawl"
        description="General crawl status and source refresh controls."
        actions={
          <>
            <Button variant="subtle" size="xs" disabled={loading || recrawling} onClick={onRefresh}>
              Refresh
            </Button>
            <Button size="xs" disabled={loading || recrawling || crawlActive} onClick={onRecrawl}>
              {recrawling ? "Requesting..." : crawlActive ? "Crawl active" : "Request recrawl"}
            </Button>
            <Button
              variant="subtle"
              size="xs"
              disabled={!crawlActive || crawlStopping || stopping}
              onClick={onStop}
            >
              {stopping || crawlStopping ? "Stopping..." : "Stop"}
            </Button>
          </>
        }
      />
      <Text className="crawl-status-message" c="dimmed" mt="md" size="sm">
        {message}
      </Text>
      <SimpleGrid className="crawl-status-grid" cols={{ base: 1, sm: 2, md: 5 }} mt="md">
        <MetricCard label="Status" value={status} />
        <MetricCard label="Pages" value={loading ? "..." : pageCount} />
        <MetricCard label="Images" value={loading ? "..." : imageCount} />
        <MetricCard label="Page errors" value={loading ? "..." : failedPages} />
        <MetricCard label="Last indexed" value={formatDateTime(lastGenerated)} />
      </SimpleGrid>
    </AppCard>
  )
}

function seoDiagnostics(crawlState: SiteCrawlState | null) {
  const pages = crawlState?.pages ?? []
  const indexedPages = pages.filter((page) => page.status === "indexed")
  const failedPages = pages.filter((page) => page.status === "failed")
  const missingTitle = indexedPages.filter((page) => !page.title?.trim())
  const missingDescription = indexedPages.filter((page) => !page.description?.trim())
  const missingCanonical = indexedPages.filter((page) => !page.canonicalUrl?.trim())
  const withOgImage = indexedPages.filter((page) => Boolean(page.ogImageUrl?.trim())).length
  const withMetadata = indexedPages.filter(
    (page) => Boolean(page.title?.trim()) && Boolean(page.description?.trim())
  ).length
  const metadataCoverage =
    indexedPages.length === 0 ? 0 : Math.round((withMetadata / indexedPages.length) * 100)
  const issueCount =
    failedPages.length + missingTitle.length + missingDescription.length + missingCanonical.length

  return {
    pages,
    indexedPages,
    indexed: indexedPages.length,
    failed: failedPages.length,
    withCanonical: indexedPages.length - missingCanonical.length,
    withOgImage,
    metadataCoverage,
    issueCount,
    checks: [
      {
        label: "Failed pages",
        count: failedPages.length,
        level: "red",
        detail: "Pages the crawler could not index.",
      },
      {
        label: "Missing titles",
        count: missingTitle.length,
        level: "yellow",
        detail: "Indexed pages without a document title.",
      },
      {
        label: "Missing descriptions",
        count: missingDescription.length,
        level: "yellow",
        detail: "Indexed pages without a meta description.",
      },
      {
        label: "Missing canonicals",
        count: missingCanonical.length,
        level: "yellow",
        detail: "Indexed pages without a canonical URL.",
      },
    ],
  }
}

function seoTopPages(crawlState: SiteCrawlState | null): SiteCrawlPageRecord[] {
  return [...(crawlState?.pages ?? [])]
    .sort((left, right) => {
      const statusOrder = statusSeoOrder(left.status) - statusSeoOrder(right.status)
      if (statusOrder !== 0) return statusOrder
      return left.depth - right.depth || left.url.localeCompare(right.url)
    })
    .slice(0, 10)
}

function statusSeoOrder(status: string): number {
  return status === "indexed" ? 0 : status === "failed" ? 2 : 1
}

function seoAdvice(
  crawlState: SiteCrawlState | null,
  linkedSearchConsole: boolean,
  seoState: SiteSeoState | null
): Array<{ title: string; detail: string; level: "green" | "yellow" | "red" | "blue" }> {
  const diagnostics = seoDiagnostics(crawlState)
  const indexedPages = diagnostics.indexedPages
  const advice: Array<{
    title: string
    detail: string
    level: "green" | "yellow" | "red" | "blue"
  }> = []

  if (!linkedSearchConsole) {
    advice.push({
      title: "Link Search Console for query visibility.",
      detail:
        "Crawl checks are available now, but organic query and landing-page performance need Search Console.",
      level: "blue",
    })
  }

  const densePages = indexedPages.filter((page) => seoCopyWordCount(page) >= 120)
  if (indexedPages.length > 0 && densePages.length < Math.min(3, indexedPages.length)) {
    advice.push({
      title: "Add more dense, indexable copy.",
      detail:
        "The crawl found only a small number of pages with enough body copy to explain the offer, use cases, and buyer intent.",
      level: "yellow",
    })
  }

  if (diagnostics.indexed > 0 && diagnostics.indexed < 5) {
    advice.push({
      title: "Build out a few more crawlable pages.",
      detail:
        "A thin site can limit organic discovery and gives ad platforms fewer landing-page signals to evaluate.",
      level: "yellow",
    })
  }

  if (diagnostics.metadataCoverage < 80 && diagnostics.indexed > 0) {
    advice.push({
      title: "Improve title and description coverage.",
      detail:
        "Search snippets and landing-page interpretation are weaker when indexed pages lack titles or meta descriptions.",
      level: "yellow",
    })
  }

  if (diagnostics.failed > 0) {
    advice.push({
      title: "Fix crawl failures first.",
      detail:
        "Pages that fail for our crawler may also be hard for search crawlers or ad review systems to evaluate.",
      level: "red",
    })
  }

  if (diagnostics.indexed > 0 && diagnostics.withCanonical < diagnostics.indexed) {
    advice.push({
      title: "Add canonical URLs where missing.",
      detail:
        "Canonical tags reduce duplicate-page ambiguity and make it clearer which pages should collect ranking signals.",
      level: "yellow",
    })
  }

  const lowCtrPage = seoState?.pages
    .filter((page) => page.impressions >= 50 && page.ctr < 0.02)
    .sort((left, right) => right.impressions - left.impressions)[0]
  if (lowCtrPage) {
    advice.push({
      title: "Improve the snippet for a high-impression page.",
      detail: `${pathFromUrl(lowCtrPage.key)} has impressions but low CTR. Tighten the title and description around the visible search intent.`,
      level: "yellow",
    })
  }

  const strikingDistanceQuery = seoState?.queries
    .filter((query) => query.impressions >= 20 && query.position >= 4 && query.position <= 15)
    .sort((left, right) => right.impressions - left.impressions)[0]
  if (strikingDistanceQuery) {
    advice.push({
      title: "Build around a query that is close to ranking.",
      detail: `"${strikingDistanceQuery.key}" is getting impressions around position ${formatPosition(
        strikingDistanceQuery.position
      )}. Add clearer copy or a focused section for that intent.`,
      level: "blue",
    })
  }

  if (seoState?.status === "provider_error" && seoState.warning) {
    advice.push({
      title: "Search Console data is temporarily unavailable.",
      detail: seoState.warning,
      level: "yellow",
    })
  }

  if (advice.length === 0) {
    advice.push({
      title: "The basic crawl signals look healthy.",
      detail:
        "Search Console performance data will make this more useful once queries and landing pages are available.",
      level: "green",
    })
  }

  return advice.slice(0, 5)
}

function seoCopyWordCount(page: SiteCrawlPageRecord): number {
  return [page.title, page.description, page.excerpt]
    .filter(Boolean)
    .join(" ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length
}

function SeoPanel({
  crawlState,
  loadingCrawl,
  linkedSearchConsole,
  seoState,
  onOpenSetup,
  onRecrawl,
}: {
  crawlState: SiteCrawlState | null
  loadingCrawl: boolean
  linkedSearchConsole: boolean
  seoState: SiteSeoState | null
  onOpenSetup: () => void
  onRecrawl: () => void
}) {
  const diagnostics = seoDiagnostics(crawlState)
  const topPages = seoTopPages(crawlState)
  const advice = seoAdvice(crawlState, linkedSearchConsole, seoState)

  return (
    <div className="site-seo-panel">
      <AppCard>
        <SectionHeader
          title="SEO"
          description="Organic visibility signals from Search Console setup and the latest site crawl."
          actions={
            <>
              <Button size="xs" variant="light" type="button" onClick={onOpenSetup}>
                Search Console
              </Button>
              <Button size="xs" type="button" disabled={loadingCrawl} onClick={onRecrawl}>
                {loadingCrawl ? "Refreshing..." : "Recrawl"}
              </Button>
            </>
          }
        />
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mt="md">
          <MetricCard
            label="Search Console"
            value={linkedSearchConsole ? "Linked" : "Not linked"}
          />
          <MetricCard label="Indexed pages" value={loadingCrawl ? "..." : diagnostics.indexed} />
          <MetricCard
            label="Metadata coverage"
            value={loadingCrawl ? "..." : `${String(diagnostics.metadataCoverage)}%`}
          />
          <MetricCard label="Crawl issues" value={loadingCrawl ? "..." : diagnostics.issueCount} />
        </SimpleGrid>
      </AppCard>

      <AppCard>
        <SectionHeader
          title="Organic Performance"
          description={
            seoState?.status === "available"
              ? `Search Console, ${seoState.window.startDate} to ${seoState.window.endDate}.`
              : "Search Console performance appears here once the site is linked."
          }
          actions={
            seoState?.reconnectUrl ? (
              <Button component="a" href={seoState.reconnectUrl} size="xs">
                Reconnect Google
              </Button>
            ) : null
          }
        />
        {seoState?.warning ? (
          <Text c="dimmed" mt="sm" size="sm">
            {seoState.warning}
          </Text>
        ) : null}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mt="md">
          <MetricCard label="Clicks" value={seoState?.totals ? seoState.totals.clicks : "-"} />
          <MetricCard
            label="Impressions"
            value={seoState?.totals ? seoState.totals.impressions : "-"}
          />
          <MetricCard
            label="CTR"
            value={seoState?.totals ? formatPercent(seoState.totals.ctr) : "-"}
          />
          <MetricCard
            label="Avg. position"
            value={seoState?.totals ? formatPosition(seoState.totals.position) : "-"}
          />
        </SimpleGrid>
      </AppCard>

      <AppCard>
        <SectionHeader
          title="Overall Advice"
          description="Practical SEO fixes inferred from the current crawl."
        />
        <div className="seo-advice-list">
          {advice.map((item) => (
            <div className="seo-advice-row" key={item.title}>
              <StatusBadge color={item.level}>{statusLabel(item.level)}</StatusBadge>
              <div>
                <strong>{item.title}</strong>
                <small>{item.detail}</small>
              </div>
            </div>
          ))}
        </div>
      </AppCard>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <AppCard>
          <SectionHeader title="Crawl Health" description="Page-level checks from the crawl." />
          <div className="seo-check-list">
            {diagnostics.checks.map((check) => (
              <div className="seo-check-row" key={check.label}>
                <StatusBadge color={check.count === 0 ? "green" : check.level}>
                  {check.count === 0 ? "Clear" : String(check.count)}
                </StatusBadge>
                <div>
                  <strong>{check.label}</strong>
                  <small>{check.detail}</small>
                </div>
              </div>
            ))}
          </div>
        </AppCard>

        <AppCard>
          <SectionHeader title="Coverage" description="How much of the crawl is usable." />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <MetricCard label="Successful" value={diagnostics.indexed} />
            <MetricCard label="Failed" value={diagnostics.failed} />
            <MetricCard label="With canonical" value={diagnostics.withCanonical} />
            <MetricCard label="With social image" value={diagnostics.withOgImage} />
          </SimpleGrid>
        </AppCard>
      </SimpleGrid>

      <AppCard>
        <SectionHeader
          title="Top Crawl Pages"
          description="Shallow indexed pages are usually the first SEO surface."
        />
        {topPages.length > 0 ? (
          <DataTableShell>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Page</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Indexed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {topPages.map((page) => (
                  <Table.Tr key={page.crawlPageId}>
                    <Table.Td>
                      <a href={page.url} target="_blank" rel="noreferrer">
                        {pathFromUrl(page.url)}
                      </a>
                    </Table.Td>
                    <Table.Td>{statusLabel(page.status)}</Table.Td>
                    <Table.Td>{page.title?.trim() || "Missing"}</Table.Td>
                    <Table.Td>{page.description?.trim() || "Missing"}</Table.Td>
                    <Table.Td>{formatDateTime(page.indexedAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </DataTableShell>
        ) : (
          <EmptyState>No crawl pages yet. Run a crawl to populate SEO page diagnostics.</EmptyState>
        )}
      </AppCard>

      {seoState?.status === "available" ? (
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <SearchConsoleTable title="Top Queries" rows={seoState.queries} />
          <SearchConsoleTable title="Top Landing Pages" rows={seoState.pages} paths />
        </SimpleGrid>
      ) : null}
    </div>
  )
}

function SearchConsoleTable({
  title,
  rows,
  paths = false,
}: {
  title: string
  rows: Array<{ key: string; clicks: number; impressions: number; ctr: number; position: number }>
  paths?: boolean
}) {
  return (
    <AppCard>
      <SectionHeader title={title} description="Last 28 days from Search Console." />
      {rows.length > 0 ? (
        <DataTableShell>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{paths ? "Page" : "Query"}</Table.Th>
                <Table.Th>Clicks</Table.Th>
                <Table.Th>Impr.</Table.Th>
                <Table.Th>CTR</Table.Th>
                <Table.Th>Pos.</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.key}>
                  <Table.Td>{paths ? pathFromUrl(row.key) : row.key}</Table.Td>
                  <Table.Td>{row.clicks}</Table.Td>
                  <Table.Td>{row.impressions}</Table.Td>
                  <Table.Td>{formatPercent(row.ctr)}</Table.Td>
                  <Table.Td>{formatPosition(row.position)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      ) : (
        <EmptyState>No Search Console rows returned for this window.</EmptyState>
      )}
    </AppCard>
  )
}

function QueuedJobStatusPanel({
  job,
  tree,
  stopping,
  retrying,
  onStop,
  onRetryFailed,
}: {
  job: JobRecord
  tree?: JobTreeRecord | null
  stopping: boolean
  retrying: boolean
  onStop: () => void
  onRetryFailed: () => void
}) {
  const displayJob = tree?.root ?? job
  const active = isJobActive(displayJob)
  const stoppingStatus = displayJob.status === "stopping"
  const progress = jobProgress(displayJob)
  const canRetryFailed = !active && hasRetryableImageFailures(displayJob)
  const rowStates = tree?.rowStates ?? []
  const warnings = Array.isArray(displayJob.result?.warnings)
    ? displayJob.result.warnings.filter((warning): warning is string => typeof warning === "string")
    : []
  return (
    <section className="queued-job-panel">
      <div>
        <strong>{statusLabel(tree?.rollupStatus ?? displayJob.status)}</strong>
        <p>{queuedJobTreeStatusMessage(tree ?? null, displayJob)}</p>
        {warnings.length > 0 ? (
          <ul className="queued-job-panel__warnings">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        ) : null}
        {rowStates.length > 0 ? (
          <ul className="queued-job-panel__rows" aria-label="Job row statuses">
            {rowStates.map((row) => (
              <li key={row.entityKey}>
                <span>{row.label ?? row.entityId ?? row.entityKey}</span>
                <small>{statusLabel(row.status)}</small>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {progress ? (
        <div className="queued-job-panel__metrics">
          <span>{String(progress.completed)} complete</span>
          <span>{String(progress.failed)} skipped</span>
          <span>{String(progress.total)} total</span>
        </div>
      ) : null}
      <div className="queued-job-panel__actions">
        {canRetryFailed ? (
          <button
            className="button button--table"
            type="button"
            disabled={retrying}
            onClick={onRetryFailed}
          >
            {retrying ? "Queueing..." : "Retry skipped variants"}
          </button>
        ) : null}
        <button
          className="button button--ghost button--table"
          type="button"
          disabled={!active || stopping || stoppingStatus}
          onClick={onStop}
        >
          {stopping || stoppingStatus ? "Stopping..." : "Cancel"}
        </button>
      </div>
    </section>
  )
}

function SegmentsPanel({
  segments,
  selectedSegment,
  assets,
  mediaLibraryAssets,
  activeAssetTab,
  segmentForm,
  assetForm,
  mediaUploadForm,
  savingSegment,
  savingAsset,
  attachingMediaAssetId,
  uploadingMedia,
  generatingAssets,
  segmentGenerationJob,
  copyGenerationJob,
  generationJob,
  targetingGenerationJob,
  stoppingGeneration,
  retryingGeneration,
  resolvingAssetId,
  campaignGeneration,
  onCreateSegment,
  onCreateAsset,
  onAttachMediaAsset,
  onDeleteAsset,
  onDeleteAssets,
  onGenerate,
  onResolveAsset,
  onDismissProviderLocation,
  onUpdateImageGroup,
  onRetryFailedGeneration,
  onSelectAssetTab,
  onSelectSegment,
  onStopGeneration,
  onUpdateAsset,
  onUploadMedia,
  onUpdateSegmentForm,
  onUpdateAssetForm,
  onUpdateMediaUploadForm,
}: {
  segments: SiteSegmentRecord[]
  selectedSegment: SiteSegmentRecord | null
  assets: SiteAssetRecord[]
  mediaLibraryAssets: SiteAssetRecord[]
  activeAssetTab: AssetTab
  segmentForm: SegmentFormState
  assetForm: AssetFormState
  mediaUploadForm: MediaUploadFormState
  savingSegment: boolean
  savingAsset: boolean
  attachingMediaAssetId: string | null
  uploadingMedia: boolean
  generatingAssets: boolean
  segmentGenerationJob: JobRecord | null
  copyGenerationJob: JobRecord | null
  generationJob: JobRecord | null
  targetingGenerationJob: JobRecord | null
  stoppingGeneration: boolean
  retryingGeneration: boolean
  resolvingAssetId: string | null
  campaignGeneration: CampaignGenerationState | null
  onCreateSegment: (event: FormEvent<HTMLFormElement>) => void
  onCreateAsset: (event: FormEvent<HTMLFormElement>) => void
  onAttachMediaAsset: (assetId: string) => void
  onDeleteAsset: (assetId: string) => void
  onDeleteAssets: (assetIds: string[]) => void
  onGenerate: (scope: string) => void
  onResolveAsset: (assetId: string) => void
  onDismissProviderLocation: (assetId: string, provider: string, locationKey: string) => void
  onUpdateImageGroup: (assets: SiteAssetRecord[], title: string) => void
  onRetryFailedGeneration: (jobId: string) => void
  onSelectAssetTab: (assetTab: AssetTab) => void
  onSelectSegment: (segmentId: string | null) => void
  onStopGeneration: (jobId: string) => void
  onUpdateAsset: (assetId: string, form: AssetFormState, asset?: SiteAssetRecord | null) => void
  onUploadMedia: (event: FormEvent<HTMLFormElement>) => void
  onUpdateSegmentForm: (value: SegmentFormState) => void
  onUpdateAssetForm: (value: AssetFormState) => void
  onUpdateMediaUploadForm: (value: MediaUploadFormState) => void
}) {
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<AssetFormState>(emptyAssetForm)
  const [assetTypeFilter, setAssetTypeFilter] = useState<SiteAssetType | "all">("all")
  const activeAssetConfig = assetTabs.find((tab) => tab.id === activeAssetTab) ?? assetTabs[0]
  const activeAssetTypes = activeAssetConfig.types ?? assetTypes
  const tabAssets =
    activeAssetConfig.types === null
      ? assets
      : assets.filter((asset) => activeAssetConfig.types?.includes(asset.assetType))
  const visibleAssets =
    assetTypeFilter === "all"
      ? tabAssets
      : tabAssets.filter((asset) => asset.assetType === assetTypeFilter)
  const copyGenerationActive = isJobActive(copyGenerationJob)
  const targetingGenerationActive = isJobActive(targetingGenerationJob)
  const mediaGenerationActive = isJobActive(generationJob)
  const segmentGenerationActive = isJobActive(segmentGenerationJob)
  const generationButtonDisabled =
    generatingAssets ||
    segmentGenerationActive ||
    (activeAssetTab === "targeting" && targetingGenerationActive) ||
    (activeAssetTab === "copy" && copyGenerationActive) ||
    (activeAssetTab === "media" && mediaGenerationActive)
  const readinessBySegmentId = useMemo(
    () =>
      new Map(
        (campaignGeneration?.segments ?? []).map((segmentState) => [
          segmentState.segment.segmentId,
          segmentState.readiness,
        ])
      ),
    [campaignGeneration]
  )

  useEffect(() => {
    const nextType = activeAssetTypes[0]
    if (nextType && !activeAssetTypes.includes(assetForm.assetType)) {
      onUpdateAssetForm({ ...assetForm, assetType: nextType, value: "" })
    }
  }, [activeAssetTypes, assetForm, onUpdateAssetForm])

  useEffect(() => {
    if (assetTypeFilter !== "all" && !activeAssetTypes.includes(assetTypeFilter)) {
      setAssetTypeFilter("all")
    }
  }, [activeAssetTypes, assetTypeFilter])

  function selectAssetTab(tab: (typeof assetTabs)[number]) {
    onSelectAssetTab(tab.id)
    const tabTypes = tab.types
    const nextType = tabTypes?.[0]
    if (nextType && tabTypes && !tabTypes.includes(assetForm.assetType)) {
      onUpdateAssetForm({ ...assetForm, assetType: nextType, value: "" })
    }
  }

  if (!selectedSegment) {
    return (
      <section className="dashboard-card">
        <div className="table-header">
          <h2>Segments</h2>
          <button
            className="button button--table"
            disabled={generatingAssets || segmentGenerationActive}
            type="button"
            onClick={() => onGenerate("Segment")}
          >
            {generatingAssets || segmentGenerationActive ? "Generating..." : "Generate segments"}
          </button>
        </div>
        <div className="segment-table-wrap">
          <table className="data-table segment-table">
            <thead>
              <tr>
                <th>Segment</th>
                <th>Type</th>
                <th>Assets</th>
                <th>Readiness</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {segments.map((segment) => (
                <tr key={segment.segmentId}>
                  <td>
                    <button
                      className="table-name-link"
                      type="button"
                      onClick={() => onSelectSegment(segment.segmentId)}
                    >
                      {segment.name}
                    </button>
                  </td>
                  <td>{segment.segmentType === "default" ? "Default" : "Segment"}</td>
                  <td>{segment.assetCount}</td>
                  <td>
                    <SegmentReadinessRollup
                      readiness={readinessBySegmentId.get(segment.segmentId)}
                    />
                  </td>
                  <td>{segment.assetCount > 0 ? "Inputs started" : "Needs inputs"}</td>
                  <td className="data-table__actions">
                    <button
                      className="button button--table"
                      type="button"
                      onClick={() => onSelectSegment(segment.segmentId)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="compact-form compact-form--table" onSubmit={onCreateSegment}>
          <input
            required
            type="text"
            placeholder="Tampa Buyers"
            value={segmentForm.name}
            onChange={(event) => onUpdateSegmentForm({ name: event.target.value })}
          />
          <button className="button button--table" type="submit" disabled={savingSegment}>
            {savingSegment ? "Adding..." : "Add segment"}
          </button>
        </form>
      </section>
    )
  }

  return (
    <section className="dashboard-card">
      <div className="segment-detail-header">
        <button className="segment-back-link" type="button" onClick={() => onSelectSegment(null)}>
          Back to segments
        </button>
        <div>
          <h2>{selectedSegment.name}</h2>
          <p>
            {selectedSegment.segmentType === "default" ? "Default segment" : "Segment"} ·{" "}
            {selectedSegment.assetCount} assets
          </p>
        </div>
        {activeAssetTab === "media" ? (
          <Group gap="xs">
            <button
              className="button button--table"
              disabled={generationButtonDisabled}
              type="button"
              onClick={() => onGenerate("image")}
            >
              {generatingAssets
                ? "Preparing..."
                : mediaGenerationActive
                  ? "Generation running"
                  : "Generate images"}
            </button>
            <button
              className="button button--table"
              disabled
              title="Video generation is paused while the next video pipeline is designed."
              type="button"
              onClick={() => onGenerate("video")}
            >
              Video generation paused
            </button>
          </Group>
        ) : (
          <button
            className="button button--table"
            disabled={generationButtonDisabled}
            type="button"
            onClick={() => onGenerate(activeAssetTab)}
          >
            {generatingAssets
              ? "Preparing..."
              : (activeAssetTab === "copy" && copyGenerationActive) ||
                  (activeAssetTab === "targeting" && targetingGenerationActive)
                ? "Generation running"
                : activeAssetTab === "overview"
                  ? "Generate all assets"
                  : `Generate ${activeAssetConfig.label}`}
          </button>
        )}
      </div>
      <nav className="tab-list asset-tab-list" aria-label="Segment asset categories">
        {assetTabs.map((tab) => (
          <button
            className={activeAssetTab === tab.id ? "tab-button tab-button--active" : "tab-button"}
            key={tab.id}
            type="button"
            onClick={() => selectAssetTab(tab)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeAssetTab === "media" && generationJob ? (
        <QueuedJobStatusPanel
          job={generationJob}
          retrying={retryingGeneration}
          stopping={stoppingGeneration}
          onStop={() => onStopGeneration(generationJob.jobId)}
          onRetryFailed={() => onRetryFailedGeneration(generationJob.jobId)}
        />
      ) : null}

      {activeAssetTab === "copy" && copyGenerationJob ? (
        <QueuedJobStatusPanel
          job={copyGenerationJob}
          retrying={false}
          stopping={stoppingGeneration}
          onStop={() => onStopGeneration(copyGenerationJob.jobId)}
          onRetryFailed={() => undefined}
        />
      ) : null}

      {activeAssetTab === "targeting" && targetingGenerationJob ? (
        <QueuedJobStatusPanel
          job={targetingGenerationJob}
          retrying={false}
          stopping={stoppingGeneration}
          onStop={() => onStopGeneration(targetingGenerationJob.jobId)}
          onRetryFailed={() => undefined}
        />
      ) : null}

      {activeAssetTab === "overview" ? (
        <SegmentOverview
          assets={assets}
          onSelectAssetTab={(assetTab) => onSelectAssetTab(assetTab)}
        />
      ) : (
        <CardSection>
          <AssetTable
            assets={visibleAssets}
            assetTypeFilter={assetTypeFilter}
            assetTypeFilterOptions={activeAssetTypes}
            editForm={editForm}
            editingAssetId={editingAssetId}
            savingAsset={savingAsset}
            resolvingAssetId={resolvingAssetId}
            onCancelEdit={() => setEditingAssetId(null)}
            onDeleteAsset={onDeleteAsset}
            onDeleteAssets={onDeleteAssets}
            onEditFormChange={setEditForm}
            onFilterAssetType={setAssetTypeFilter}
            onResolveAsset={onResolveAsset}
            onDismissProviderLocation={onDismissProviderLocation}
            onUpdateImageGroup={onUpdateImageGroup}
            onSaveEdit={(asset, form) => {
              onUpdateAsset(asset.assetId, form, asset)
              setEditingAssetId(null)
            }}
            onStartEdit={(asset) => {
              setEditingAssetId(asset.assetId)
              setEditForm(assetFormFromRecord(asset))
            }}
          />
        </CardSection>
      )}

      {activeAssetTab !== "overview" && activeAssetTab !== "media" ? (
        <CardSection>
          <form onSubmit={onCreateAsset}>
            <SimpleGrid cols={{ base: 1, md: 3 }}>
              <NativeSelect
                label="Type"
                value={assetForm.assetType}
                onChange={(event) =>
                  onUpdateAssetForm({
                    ...assetForm,
                    assetType: event.target.value as SiteAssetType,
                  })
                }
                data={activeAssetTypes.map((type) => ({
                  value: type,
                  label: assetTypeLabel(type),
                }))}
              />
              <TextInput
                label="Name"
                placeholder="Optional label"
                value={assetForm.name}
                onChange={(event) => onUpdateAssetForm({ ...assetForm, name: event.target.value })}
              />
              <TextInput
                required
                label="Value"
                placeholder={assetPlaceholder(assetForm.assetType)}
                value={assetForm.value}
                onChange={(event) => onUpdateAssetForm({ ...assetForm, value: event.target.value })}
              />
            </SimpleGrid>
            <FormActions>
              <Button type="submit" disabled={savingAsset}>
                {savingAsset ? "Adding..." : "Add asset"}
              </Button>
              {(activeAssetTab === "targeting" && isTargetingAssetType(assetForm.assetType)) ||
              (activeAssetTab === "copy" && isCopyAssetType(assetForm.assetType)) ? (
                <Button
                  variant="light"
                  disabled={generationButtonDisabled}
                  type="button"
                  onClick={() => {
                    setAssetTypeFilter(assetForm.assetType)
                    onGenerate(assetForm.assetType)
                  }}
                >
                  {(activeAssetTab === "targeting" && targetingGenerationActive) ||
                  (activeAssetTab === "copy" && copyGenerationActive)
                    ? "Generation running"
                    : `Generate ${generateAssetTypeLabel(assetForm.assetType)}`}
                </Button>
              ) : null}
            </FormActions>
          </form>
        </CardSection>
      ) : null}

      {activeAssetTab === "media" ? (
        <MediaAddPanel
          assetForm={assetForm}
          assets={assets}
          attachingMediaAssetId={attachingMediaAssetId}
          mediaLibraryAssets={mediaLibraryAssets}
          mediaUploadForm={mediaUploadForm}
          savingAsset={savingAsset}
          uploadingMedia={uploadingMedia}
          onAttachMediaAsset={onAttachMediaAsset}
          onCreateAsset={onCreateAsset}
          onUpdateAssetForm={onUpdateAssetForm}
          onUpdateMediaUploadForm={onUpdateMediaUploadForm}
          onUploadMedia={onUploadMedia}
        />
      ) : null}
    </section>
  )
}

function MediaAddPanel({
  assetForm,
  assets,
  attachingMediaAssetId,
  mediaLibraryAssets,
  mediaUploadForm,
  savingAsset,
  uploadingMedia,
  onAttachMediaAsset,
  onCreateAsset,
  onUpdateAssetForm,
  onUpdateMediaUploadForm,
  onUploadMedia,
}: {
  assetForm: AssetFormState
  assets: SiteAssetRecord[]
  attachingMediaAssetId: string | null
  mediaLibraryAssets: SiteAssetRecord[]
  mediaUploadForm: MediaUploadFormState
  savingAsset: boolean
  uploadingMedia: boolean
  onAttachMediaAsset: (assetId: string) => void
  onCreateAsset: (event: FormEvent<HTMLFormElement>) => void
  onUpdateAssetForm: (value: AssetFormState) => void
  onUpdateMediaUploadForm: (value: MediaUploadFormState) => void
  onUploadMedia: (event: FormEvent<HTMLFormElement>) => void
}) {
  const [method, setMethod] = useState<MediaAddMethod>("library")
  const [zoomedAsset, setZoomedAsset] = useState<SiteAssetRecord | null>(null)
  const mediaAssetType = assetForm.assetType === "video" ? "video" : "image"
  const attachedAssetIds = new Set(assets.map((asset) => asset.assetId))
  const availableLibraryAssets = mediaLibraryAssets.filter(
    (asset) => !attachedAssetIds.has(asset.assetId)
  )

  return (
    <AppCard className="manual-add-panel">
      <Group className="manual-add-panel__header" justify="space-between" align="center">
        <Text fw={700}>Add media</Text>
        <SegmentedControl
          aria-label="Media add methods"
          size="xs"
          value={method}
          data={mediaAddMethods.map((option) => ({ value: option.id, label: option.label }))}
          onChange={(value) => setMethod(value as MediaAddMethod)}
        />
      </Group>
      {method === "library" ? (
        <div className="media-library-picker">
          {availableLibraryAssets.length > 0 ? (
            availableLibraryAssets.map((asset) => (
              <div className="media-library-picker__item" key={asset.assetId}>
                <AssetValue asset={asset} showMediaName={false} onOpenMedia={setZoomedAsset} />
                <div>
                  <strong>{asset.name || assetTypeLabel(asset.assetType)}</strong>
                  <span>{asset.source}</span>
                </div>
                <Button
                  size="xs"
                  disabled={attachingMediaAssetId === asset.assetId}
                  type="button"
                  onClick={() => onAttachMediaAsset(asset.assetId)}
                >
                  {attachingMediaAssetId === asset.assetId ? "Adding..." : "Add"}
                </Button>
              </div>
            ))
          ) : (
            <EmptyState>No reusable media outside this segment.</EmptyState>
          )}
        </div>
      ) : null}
      {method === "upload" ? (
        <form onSubmit={onUploadMedia}>
          <SimpleGrid cols={{ base: 1, md: 2 }} mt="md">
            <FileInput
              required
              accept="image/*,video/*"
              label="File"
              description="Images and videos up to 100 MB."
              value={mediaUploadForm.file}
              onChange={(file) => onUpdateMediaUploadForm({ ...mediaUploadForm, file })}
            />
            <TextInput
              label="Name"
              placeholder="Optional media label"
              value={mediaUploadForm.name}
              onChange={(event) =>
                onUpdateMediaUploadForm({ ...mediaUploadForm, name: event.target.value })
              }
            />
          </SimpleGrid>
          <FormActions>
            <Button type="submit" disabled={uploadingMedia || !mediaUploadForm.file}>
              {uploadingMedia ? "Uploading..." : "Upload image/video"}
            </Button>
          </FormActions>
        </form>
      ) : null}
      {method === "url" ? (
        <form onSubmit={onCreateAsset}>
          <SimpleGrid cols={{ base: 1, md: 3 }} mt="md">
            <NativeSelect
              label="Type"
              value={mediaAssetType}
              onChange={(event) =>
                onUpdateAssetForm({
                  ...assetForm,
                  assetType: event.target.value as SiteAssetType,
                })
              }
              data={[
                { value: "image", label: "Image" },
                { value: "video", label: "Video" },
              ]}
            />
            <TextInput
              label="Name"
              placeholder="Optional label"
              value={assetForm.name}
              onChange={(event) => onUpdateAssetForm({ ...assetForm, name: event.target.value })}
            />
            <TextInput
              required
              type="url"
              label="Hosted URL"
              placeholder={assetPlaceholder(mediaAssetType)}
              value={assetForm.value}
              onChange={(event) => onUpdateAssetForm({ ...assetForm, value: event.target.value })}
            />
          </SimpleGrid>
          <FormActions>
            <Button type="submit" disabled={savingAsset}>
              {savingAsset ? "Adding..." : "Add URL asset"}
            </Button>
          </FormActions>
        </form>
      ) : null}
      {zoomedAsset ? (
        <MediaZoomModal asset={zoomedAsset} onClose={() => setZoomedAsset(null)} />
      ) : null}
    </AppCard>
  )
}

function SegmentReadinessRollup({
  readiness,
}: {
  readiness: CampaignGenerationState["segments"][number]["readiness"] | undefined
}) {
  if (!readiness) {
    return <span className="segment-readiness-rollup segment-readiness-rollup--muted">Loading</span>
  }
  const channelReadiness = readiness.channelReadiness ?? []
  const readyChannels = channelReadiness.filter((channel) => channel.ready).length
  const label =
    channelReadiness.length > 0
      ? `${String(readyChannels)}/${String(channelReadiness.length)} channels`
      : readiness.ready
        ? "Ready"
        : "Needs assets"
  return (
    <span
      className={`segment-readiness-rollup${
        readiness.ready ? " segment-readiness-rollup--ready" : ""
      }`}
    >
      <strong>{String(readiness.score)}%</strong>
      <small>{label}</small>
    </span>
  )
}

function SegmentOverview({
  assets,
  onSelectAssetTab,
}: {
  assets: SiteAssetRecord[]
  onSelectAssetTab: (assetTab: AssetTab) => void
}) {
  const categories = overviewCategories.map((category) => {
    const categoryAssets = assets.filter((asset) => category.types.includes(asset.assetType))
    return {
      ...category,
      assets: categoryAssets,
      sourceCount: new Set(categoryAssets.map((asset) => asset.source)).size,
    }
  })

  return (
    <div className="segment-overview">
      <div className="segment-overview__metrics">
        <div className="site-readiness-item">
          <span>Total assets</span>
          <strong>{assets.length}</strong>
        </div>
        <div className="site-readiness-item">
          <span>Landing pages</span>
          <strong>
            {categories.find((category) => category.id === "landing")?.assets.length ?? 0}
          </strong>
        </div>
        <div className="site-readiness-item">
          <span>Copy assets</span>
          <strong>
            {categories.find((category) => category.id === "copy")?.assets.length ?? 0}
          </strong>
        </div>
        <div className="site-readiness-item">
          <span>Media assets</span>
          <strong>
            {categories.find((category) => category.id === "media")?.assets.length ?? 0}
          </strong>
        </div>
      </div>
      <div className="segment-overview__sections">
        {categories.map((category) => (
          <section className="segment-overview__section" key={category.id}>
            <div>
              <span className="segment-overview__label">{category.label}</span>
              <strong>{category.assets.length}</strong>
              <p>{overviewCategorySummary(category.assets, category.sourceCount)}</p>
            </div>
            <button
              className="button button--ghost button--table"
              type="button"
              onClick={() => onSelectAssetTab(category.id)}
            >
              Open
            </button>
          </section>
        ))}
      </div>
    </div>
  )
}

function AssetTable({
  assets,
  assetTypeFilter,
  assetTypeFilterOptions,
  editForm,
  editingAssetId,
  savingAsset,
  resolvingAssetId,
  onCancelEdit,
  onDeleteAsset,
  onDeleteAssets,
  onEditFormChange,
  onFilterAssetType,
  onResolveAsset,
  onDismissProviderLocation,
  onUpdateImageGroup,
  onSaveEdit,
  onStartEdit,
}: {
  assets: SiteAssetRecord[]
  assetTypeFilter: SiteAssetType | "all"
  assetTypeFilterOptions: SiteAssetType[]
  editForm: AssetFormState
  editingAssetId: string | null
  savingAsset: boolean
  resolvingAssetId: string | null
  onCancelEdit: () => void
  onDeleteAsset: (assetId: string) => void
  onDeleteAssets: (assetIds: string[]) => void
  onEditFormChange: (value: AssetFormState) => void
  onFilterAssetType: (value: SiteAssetType | "all") => void
  onResolveAsset: (assetId: string) => void
  onDismissProviderLocation: (assetId: string, provider: string, locationKey: string) => void
  onUpdateImageGroup: (assets: SiteAssetRecord[], title: string) => void
  onSaveEdit: (asset: SiteAssetRecord, form: AssetFormState) => void
  onStartEdit: (asset: SiteAssetRecord) => void
}) {
  const [zoomedAsset, setZoomedAsset] = useState<SiteAssetRecord | null>(null)
  const [expandedResolutionAssetId, setExpandedResolutionAssetId] = useState<string | null>(null)
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(() => new Set())
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null)
  const [groupTitle, setGroupTitle] = useState("")
  const selectableAssetIds = assets.map((asset) => asset.assetId)
  const hasSelectableAssets = selectableAssetIds.length > 0
  const selectedVisibleAssetIds = selectableAssetIds.filter((assetId) =>
    selectedAssetIds.has(assetId)
  )
  const allSelectableAssetsSelected =
    selectableAssetIds.length > 0 && selectedVisibleAssetIds.length === selectableAssetIds.length

  useEffect(() => {
    setSelectedAssetIds((current) => {
      const visible = new Set(assets.map((asset) => asset.assetId))
      const next = new Set([...current].filter((assetId) => visible.has(assetId)))
      return next.size === current.size ? current : next
    })
  }, [assets])

  const rows = groupedAssetRows(assets)
  const columnCount = (hasSelectableAssets ? 1 : 0) + 4

  function toggleAssetSelection(assetIds: string[], checked: boolean) {
    setSelectedAssetIds((current) => {
      const next = new Set(current)
      for (const assetId of assetIds) {
        if (checked) next.add(assetId)
        else next.delete(assetId)
      }
      return next
    })
  }

  function deleteSelectedAssets() {
    const assetIds = selectedVisibleAssetIds
    onDeleteAssets(assetIds)
    setSelectedAssetIds(new Set())
  }

  function startGroupEdit(row: Extract<AssetTableRow, { type: "group" }>) {
    setEditingGroupKey(row.key)
    setGroupTitle(row.label)
  }

  function saveGroupEdit(row: Extract<AssetTableRow, { type: "group" }>) {
    const nextTitle = groupTitle.trim()
    if (!nextTitle) return
    onUpdateImageGroup(row.assets, nextTitle)
    setEditingGroupKey(null)
  }

  return (
    <div className="asset-table-wrap">
      <div className="asset-selection-toolbar">
        <div className="asset-selection-toolbar__filter">
          <NativeSelect
            label="Type"
            value={assetTypeFilter}
            onChange={(event) =>
              onFilterAssetType(
                event.target.value === "all" ? "all" : (event.target.value as SiteAssetType)
              )
            }
            data={[
              { value: "all", label: "All types" },
              ...assetTypeFilterOptions.map((type) => ({
                value: type,
                label: assetTypeLabel(type),
              })),
            ]}
          />
        </div>
        {hasSelectableAssets ? (
          <div className="asset-selection-toolbar__actions">
            <Checkbox
              checked={allSelectableAssetsSelected}
              label="Select all"
              onChange={(event) => toggleAssetSelection(selectableAssetIds, event.target.checked)}
            />
            <Button
              variant="subtle"
              size="xs"
              disabled={savingAsset || selectedVisibleAssetIds.length === 0}
              type="button"
              onClick={deleteSelectedAssets}
            >
              Remove selected
            </Button>
          </div>
        ) : null}
      </div>
      {assets.length === 0 ? <EmptyState>No assets in this view.</EmptyState> : null}
      {assets.length > 0 ? (
        <table
          className={
            hasSelectableAssets
              ? "data-table asset-table asset-table--selectable"
              : "data-table asset-table"
          }
        >
          <thead>
            <tr>
              {hasSelectableAssets ? <th aria-label="Select assets" /> : null}
              <th>Type</th>
              <th>Value</th>
              <th>Source</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              if (row.type === "group") {
                const groupAssetIds = row.assets.map((asset) => asset.assetId)
                const groupSelected = groupAssetIds.every((assetId) =>
                  selectedAssetIds.has(assetId)
                )
                const editingGroup = editingGroupKey === row.key
                return (
                  <tr className="asset-group-row" key={row.key}>
                    {hasSelectableAssets ? (
                      <td>
                        <input
                          aria-label={`Select ${row.label}`}
                          checked={groupSelected}
                          type="checkbox"
                          onChange={(event) =>
                            toggleAssetSelection(groupAssetIds, event.target.checked)
                          }
                        />
                      </td>
                    ) : null}
                    <td>{assetTypeLabel(row.assets[0]?.assetType ?? "image")}</td>
                    <td>
                      {editingGroup ? (
                        <input
                          className="table-input"
                          type="text"
                          value={groupTitle}
                          onChange={(event) => setGroupTitle(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") saveGroupEdit(row)
                            if (event.key === "Escape") setEditingGroupKey(null)
                          }}
                        />
                      ) : (
                        <button
                          className="asset-group-title-button"
                          disabled={savingAsset}
                          type="button"
                          onClick={() => startGroupEdit(row)}
                        >
                          <span className="asset-value-stack">
                            <span>{row.label}</span>
                            <small>
                              {row.assets.length} variants · {variantSummary(row.assets)}
                            </small>
                          </span>
                        </button>
                      )}
                    </td>
                    <td>{sourceSummary(row.assets)}</td>
                    <td className="data-table__actions">
                      {editingGroup ? (
                        <>
                          <button
                            className="button button--table"
                            disabled={savingAsset || !groupTitle.trim()}
                            type="button"
                            onClick={() => saveGroupEdit(row)}
                          >
                            Save
                          </button>
                          <button
                            className="button button--ghost button--table"
                            disabled={savingAsset}
                            type="button"
                            onClick={() => setEditingGroupKey(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="button button--table"
                            disabled={savingAsset}
                            type="button"
                            onClick={() => startGroupEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            className="button button--ghost button--table"
                            disabled={savingAsset}
                            type="button"
                            onClick={() => onDeleteAssets(row.assets.map((asset) => asset.assetId))}
                          >
                            Delete group
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              }

              const asset = row.asset
              const resolution = locationResolution(asset)
              const isExpanded = expandedResolutionAssetId === asset.assetId
              const isMediaAsset = asset.assetType === "image" || asset.assetType === "video"
              return editingAssetId === asset.assetId ? (
                <tr key={asset.assetId}>
                  {hasSelectableAssets ? <td /> : null}
                  <td>{assetTypeLabel(asset.assetType)}</td>
                  <td>
                    <input
                      className="table-input"
                      type="text"
                      value={isMediaAsset ? editForm.name : editForm.value}
                      onChange={(event) =>
                        onEditFormChange(
                          isMediaAsset
                            ? { ...editForm, name: event.target.value }
                            : { ...editForm, value: event.target.value }
                        )
                      }
                    />
                  </td>
                  <td>{asset.source}</td>
                  <td className="data-table__actions">
                    <button
                      className="button button--table"
                      disabled={
                        savingAsset ||
                        (isMediaAsset ? !editForm.name.trim() : !editForm.value.trim())
                      }
                      type="button"
                      onClick={() => onSaveEdit(asset, editForm)}
                    >
                      Save
                    </button>
                    <button
                      className="button button--ghost button--table"
                      disabled={savingAsset}
                      type="button"
                      onClick={onCancelEdit}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <Fragment key={asset.assetId}>
                  <tr className={assetRowClassName(asset, resolution)}>
                    {hasSelectableAssets ? (
                      <td>
                        <input
                          aria-label={`Select ${asset.name || assetTypeLabel(asset.assetType)}`}
                          checked={selectedAssetIds.has(asset.assetId)}
                          type="checkbox"
                          onChange={(event) =>
                            toggleAssetSelection([asset.assetId], event.target.checked)
                          }
                        />
                      </td>
                    ) : null}
                    <td>{assetTypeLabel(asset.assetType)}</td>
                    <td>
                      <div className="asset-value-cell">
                        <AssetValue asset={asset} onOpenMedia={setZoomedAsset} />
                        {resolution ? (
                          <button
                            className={`location-resolution-pill location-resolution-pill--${resolution.status}`}
                            type="button"
                            onClick={() =>
                              setExpandedResolutionAssetId(isExpanded ? null : asset.assetId)
                            }
                          >
                            {locationResolutionLabel(resolution.status)}
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td>{asset.source}</td>
                    <td className="data-table__actions">
                      {asset.assetType === "location" ? (
                        <button
                          className="button button--ghost button--table"
                          disabled={savingAsset || resolvingAssetId === asset.assetId}
                          type="button"
                          onClick={() => onResolveAsset(asset.assetId)}
                        >
                          {resolvingAssetId === asset.assetId ? "Resolving..." : "Resolve"}
                        </button>
                      ) : null}
                      <button
                        className="button button--table"
                        disabled={savingAsset}
                        type="button"
                        onClick={() => onStartEdit(asset)}
                      >
                        Edit
                      </button>
                      <button
                        aria-label={`Delete ${asset.name || assetTypeLabel(asset.assetType)}`}
                        className="icon-button icon-button--danger"
                        disabled={savingAsset}
                        type="button"
                        title="Delete asset"
                        onClick={() => onDeleteAsset(asset.assetId)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                  {isExpanded && resolution ? (
                    <tr className="location-resolution-details-row">
                      <td colSpan={columnCount}>
                        <LocationResolutionDetails
                          asset={asset}
                          resolution={resolution}
                          saving={savingAsset}
                          onDismiss={(provider, locationKey) =>
                            onDismissProviderLocation(asset.assetId, provider, locationKey)
                          }
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      ) : null}
      {zoomedAsset ? (
        <MediaZoomModal asset={zoomedAsset} onClose={() => setZoomedAsset(null)} />
      ) : null}
    </div>
  )
}

function AssetValue({
  asset,
  showMediaName = true,
  onOpenMedia,
}: {
  asset: SiteAssetRecord
  showMediaName?: boolean
  onOpenMedia?: (asset: SiteAssetRecord) => void
}) {
  const value = assetDisplayValue(asset)
  const distinctName = assetDistinctName(asset)
  const mediaName =
    showMediaName && (asset.assetType === "image" || asset.assetType === "video")
      ? asset.name?.trim() || null
      : null
  if (asset.assetType === "site_link" && asset.valueUrl) {
    const title = asset.valueText || asset.name || "Site link"
    return (
      <span className="asset-link-value">
        <span>{title}</span>
        {distinctName && distinctName !== title ? <small>{distinctName}</small> : null}
        <a
          className="asset-url-link"
          href={asset.valueUrl}
          rel="noreferrer"
          target="_blank"
          title={asset.valueUrl}
        >
          {value}
        </a>
      </span>
    )
  }
  if (asset.assetType === "landing_page" && asset.valueUrl) {
    return (
      <span className="asset-link-value">
        {distinctName ? <span>{distinctName}</span> : null}
        <a
          className="asset-url-link"
          href={asset.valueUrl}
          rel="noreferrer"
          target="_blank"
          title={asset.valueUrl}
        >
          {value}
        </a>
      </span>
    )
  }
  if (asset.assetType === "image" && asset.valueUrl) {
    return (
      <div className="asset-media-value">
        <button
          aria-label={`Preview ${asset.name || "Image asset"}`}
          className="asset-media-value__preview"
          type="button"
          onClick={() => onOpenMedia?.(asset)}
        >
          <img alt={asset.name || "Image asset"} src={asset.valueUrl} />
        </button>
        <span className="asset-value-stack">
          {mediaName ? <span>{mediaName}</span> : null}
          <span>{value}</span>
        </span>
      </div>
    )
  }
  if (asset.assetType === "video" && asset.valueUrl) {
    return (
      <div className="asset-media-value">
        <button
          aria-label={`Preview ${asset.name || "Video asset"}`}
          className="asset-media-value__preview asset-media-value__preview--video"
          type="button"
          onClick={() => onOpenMedia?.(asset)}
        >
          <video muted preload="metadata" src={asset.valueUrl}>
            <track kind="captions" />
          </video>
          <span aria-hidden="true" className="asset-media-value__play">
            ▶
          </span>
        </button>
        <span className="asset-value-stack">
          {mediaName ? <span>{mediaName}</span> : null}
          <span>{value}</span>
        </span>
      </div>
    )
  }
  if (distinctName) {
    return (
      <span className="asset-value-stack">
        <span>{distinctName}</span>
        <span>{value}</span>
      </span>
    )
  }
  return <span>{value}</span>
}

function LocationResolutionDetails({
  asset,
  resolution,
  saving,
  onDismiss,
}: {
  asset: SiteAssetRecord
  resolution: LocationResolutionRecord
  saving: boolean
  onDismiss: (provider: string, locationKey: string) => void
}) {
  const providers = Object.entries(resolution.providers)
  const hiddenKeys = locationHiddenProviderLocationKeys(asset)
  return (
    <div className="location-resolution-details">
      <div>
        <span>Resolved as</span>
        <strong>{resolution.normalized?.label ?? "Needs input"}</strong>
      </div>
      {resolution.message ? <p>{resolution.message}</p> : null}
      {resolution.candidates.length > 0 ? (
        <div>
          <span>Candidates</span>
          <ul>
            {resolution.candidates.map((candidate) => (
              <li key={candidate.label}>{candidate.label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {providers.length > 0 ? (
        <div>
          <span>Provider checks</span>
          <ul>
            {providers.map(([provider, status]) => {
              const locations = (status.locations ?? []).filter((location) => {
                const key = providerLocationKey(location)
                return key && !hiddenKeys.has(providerLocationDismissKey(provider, key))
              })
              return (
                <li key={provider}>
                  <strong>{providerLabel(provider)}:</strong>{" "}
                  {locationProviderStatusLabel(status.status)}. {status.message}
                  {locations.length > 0 ? (
                    <div className="location-match-badges">
                      {locations.map((location) => {
                        const key = providerLocationKey(location)
                        if (!key) return null
                        return (
                          <span className="location-match-badge" key={`${provider}:${key}`}>
                            {providerLocationLabel(location)}
                            <button
                              aria-label={`Hide ${providerLocationLabel(location)}`}
                              disabled={saving}
                              type="button"
                              onClick={() => onDismiss(provider, key)}
                            >
                              ×
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function MediaZoomModal({ asset, onClose }: { asset: SiteAssetRecord; onClose: () => void }) {
  if (!asset.valueUrl) return null
  const isVideo = asset.assetType === "video"
  return (
    <div className="media-zoom">
      <button
        aria-label="Close preview"
        className="media-zoom__backdrop"
        type="button"
        onClick={onClose}
      />
      <div
        aria-label={asset.name || (isVideo ? "Video preview" : "Image preview")}
        aria-modal="true"
        className="media-zoom__dialog"
        role="dialog"
      >
        <div className="media-zoom__header">
          <strong>{asset.name || assetTypeLabel(asset.assetType)}</strong>
          <button
            aria-label="Close preview"
            className="icon-button"
            type="button"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {isVideo ? (
          <video autoPlay controls src={asset.valueUrl}>
            <track kind="captions" />
          </video>
        ) : (
          <img alt={asset.name || "Image asset"} src={asset.valueUrl} />
        )}
      </div>
    </div>
  )
}

function TrackingPanel({
  tracking,
  realtimeMinutes,
  realtimeSummary,
  form,
  saving,
  checkingRealtime,
  validatingConversion,
  validationModalOpen,
  onCheckRealtime,
  onCloseValidationModal,
  onCreateMapping,
  onGenerateMappings,
  onOpenValidationModal,
  onOpenSetup,
  onSelectRealtimeMinutes,
  onSyncEvents,
  onUpdateForm,
  onUpdateStatus,
  onValidateAnyConversion,
  onValidateConversion,
  onCreateMappingFromSummary,
  onCreateMappingFromJourney,
}: {
  tracking: SiteTrackingPayload | null
  realtimeMinutes: number
  realtimeSummary: RealtimeTrackingEventsPayload | null
  form: TrackingMappingFormState
  saving: boolean
  checkingRealtime: boolean
  validatingConversion: boolean
  validationModalOpen: boolean
  onCheckRealtime: () => void
  onCloseValidationModal: () => void
  onCreateMapping: (event: FormEvent<HTMLFormElement>) => void
  onGenerateMappings: () => void
  onOpenValidationModal: () => void
  onOpenSetup: () => void
  onSelectRealtimeMinutes: (minutes: number) => void
  onSyncEvents: () => void
  onUpdateForm: (value: TrackingMappingFormState) => void
  onUpdateStatus: (mappingId: string, status: SiteEventMapping["status"]) => void
  onValidateAnyConversion: () => void
  onValidateConversion: (mappingId: string) => void
  onCreateMappingFromSummary: (summary: CapturedEventSummary) => void
  onCreateMappingFromJourney: (summary: CapturedJourneySummary) => void
}) {
  const patterns = tracking?.observedPatterns ?? []
  const eventSummaries = tracking?.eventSummaries ?? []
  const journeySummaries = tracking?.journeySummaries ?? []
  const mappings = tracking?.mappings ?? []
  const activeMappings = mappings.filter((mapping) => mapping.status === "active")
  const validationMappings = [...activeMappings].sort(compareValidationMappings)
  const preferredValidationMapping =
    preferredRealtimeValidationMapping(validationMappings, realtimeSummary) ??
    validationMappings[0] ??
    null
  const mappedJourneySummaryIds = new Set(
    mappings
      .map((mapping) => mapping.valueJson?.match)
      .map((match) =>
        match && typeof match === "object" && "journeySummaryId" in match
          ? String(match.journeySummaryId)
          : ""
      )
      .filter(Boolean)
  )
  const [selectedValidationMappingId, setSelectedValidationMappingId] = useState("")
  const selectedValidationMapping =
    validationMappings.find((mapping) => mapping.mappingId === selectedValidationMappingId) ??
    preferredValidationMapping ??
    null
  const validationLeadSnippet = `window.adgiroEvent("lead", {
  email: currentUser.email,
  phone: currentUser.phone,
  accountId: currentUser.id,
  firstName: currentUser.firstName,
  lastName: currentUser.lastName,
  transactionId: "lead_" + currentUser.id,
  value: 250,
  currency: "USD"
})`
  const measurementReady = Boolean(tracking?.config.ga4MeasurementId)
  const deliveryValidated = (tracking?.conversionValidation?.results ?? []).some(
    (result) => result.status === "passed"
  )
  const readinessSteps = [
    {
      label: "Measurement",
      ready: measurementReady,
      message: tracking?.config.ga4MeasurementId ?? "Set up on Setup tab",
    },
    {
      label: "Events",
      ready: patterns.length > 0,
      message: patterns.length > 0 ? `${String(patterns.length)} events seen` : "No events yet",
    },
    {
      label: "Conversions",
      ready: activeMappings.length > 0,
      message:
        activeMappings.length > 0 ? `${String(activeMappings.length)} mapped` : "None mapped",
    },
    {
      label: "Delivery",
      ready: deliveryValidated,
      message: deliveryValidated ? "Validated" : "Not validated",
    },
  ]
  return (
    <div className="tracking-panel">
      <div className="tracking-readiness-strip">
        {readinessSteps.map((step) => (
          <div
            className={`setup-readiness-step${step.ready ? " setup-readiness-step--ready" : ""}`}
            key={step.label}
          >
            <StatusBadge color={step.ready ? "green" : "gray"}>
              {step.ready ? "Ready" : "Needed"}
            </StatusBadge>
            <strong>{step.label}</strong>
            <span>{step.message}</span>
          </div>
        ))}
      </div>
      {!measurementReady ? (
        <EmptyState>
          Set up measurement on the{" "}
          <button className="inline-link-button" type="button" onClick={onOpenSetup}>
            Setup
          </button>{" "}
          tab before installing the tracking tag.
        </EmptyState>
      ) : null}
      <AppCard className="tracking-install" p="md">
        <div>
          <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
            Install script
          </Text>
          <Code block>{tracking?.installSnippet ?? "Loading tracking script..."}</Code>
        </div>
        <CopyButton value={tracking?.installSnippet ?? ""}>
          {({ copied, copy }) => (
            <Button
              size="xs"
              disabled={!tracking?.installSnippet || !measurementReady}
              type="button"
              onClick={copy}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          )}
        </CopyButton>
      </AppCard>

      <SectionHeader
        title="Realtime event summary"
        description="Counts from GA4 Realtime for quick install checks."
        actions={
          <>
            <NativeSelect
              aria-label="Realtime window"
              disabled={checkingRealtime || !measurementReady}
              value={String(realtimeMinutes)}
              onChange={(event) => onSelectRealtimeMinutes(Number(event.target.value))}
              data={[
                { value: "5", label: "Last 5 minutes" },
                { value: "15", label: "Last 15 minutes" },
                { value: "30", label: "Last 30 minutes" },
              ]}
            />
            <Button
              variant="subtle"
              size="xs"
              disabled={checkingRealtime || !measurementReady}
              type="button"
              onClick={onCheckRealtime}
            >
              {checkingRealtime ? "Checking..." : "Check realtime"}
            </Button>
          </>
        }
      />
      <TrackingRealtimeSummary summary={realtimeSummary} />

      <SectionHeader
        title="Conversion mappings"
        description="Events come from GA4. Generated mappings are active immediately."
        actions={
          <>
            <Button
              variant="subtle"
              size="xs"
              disabled={saving || !measurementReady || activeMappings.length === 0}
              type="button"
              onClick={onOpenValidationModal}
            >
              Validate delivery
            </Button>
            <Button
              variant="subtle"
              size="xs"
              disabled={saving || !measurementReady}
              type="button"
              onClick={onSyncEvents}
            >
              Sync GA4
            </Button>
            <Button
              size="xs"
              disabled={saving || !measurementReady}
              type="button"
              onClick={onGenerateMappings}
            >
              {saving ? "Working..." : "Generate mappings"}
            </Button>
          </>
        }
      />

      <TrackingMappingsTable mappings={mappings} saving={saving} onUpdateStatus={onUpdateStatus} />
      <ConversionValidationCard validation={tracking?.conversionValidation ?? null} />
      <TrackingJourneySummariesTable
        summaries={journeySummaries}
        saving={saving}
        mappedJourneySummaryIds={mappedJourneySummaryIds}
        onCreateMapping={onCreateMappingFromJourney}
      />
      <TrackingEventSummariesTable
        summaries={eventSummaries}
        saving={saving}
        onCreateMapping={onCreateMappingFromSummary}
      />

      <Modal
        opened={validationModalOpen}
        onClose={onCloseValidationModal}
        title="Validate conversion delivery"
        centered
      >
        <Text size="sm">
          Complete a real test conversion on your site, using a test lead or account you can
          identify. Keep this window open, then click Validate now after the conversion finishes.
        </Text>
        <Text c="dimmed" size="xs" mt="sm">
          If your app knows the lead from a form, signup, or OAuth callback, fire this after the
          lead is created. The tracker hashes identity fields before forwarding.
        </Text>
        <Code block mt="xs" className="conversion-validation-modal__code">
          {validationLeadSnippet}
        </Code>
        <CopyButton value={validationLeadSnippet}>
          {({ copied, copy }) => (
            <Button mt="xs" size="xs" variant="subtle" type="button" onClick={copy}>
              {copied ? "Copied" : "Copy lead snippet"}
            </Button>
          )}
        </CopyButton>
        <NativeSelect
          mt="md"
          label="Conversion"
          value={selectedValidationMapping?.mappingId ?? ""}
          onChange={(event) => setSelectedValidationMappingId(event.target.value)}
          data={validationMappings.map((mapping) => ({
            value: mapping.mappingId,
            label: validationMappingLabel(mapping),
          }))}
        />
        {selectedValidationMapping ? (
          <div className="conversion-validation-modal__mapping">
            <span>
              <strong>GA4:</strong> {selectedValidationMapping.ga4EventName}
            </span>
            <span>
              <strong>Meta:</strong> {selectedValidationMapping.metaEventName}
            </span>
          </div>
        ) : null}
        <Text c="dimmed" size="xs" mt="sm">
          Use a real form/signup flow. Do not use fake browser events or manual API calls.
        </Text>
        <FormActions>
          <Button variant="subtle" type="button" onClick={onCloseValidationModal}>
            Cancel
          </Button>
          <Button
            variant="light"
            disabled={activeMappings.length === 0 || validatingConversion}
            type="button"
            onClick={onValidateAnyConversion}
          >
            {validatingConversion ? "Validating..." : "Validate any"}
          </Button>
          <Button
            disabled={!selectedValidationMapping || validatingConversion}
            type="button"
            onClick={() =>
              selectedValidationMapping
                ? onValidateConversion(selectedValidationMapping.mappingId)
                : undefined
            }
          >
            {validatingConversion ? "Validating..." : "Validate selected"}
          </Button>
        </FormActions>
      </Modal>

      <form onSubmit={onCreateMapping}>
        <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} mt="md">
          <NativeSelect
            label="GA4 event"
            value={form.patternId}
            onChange={(event) => {
              const pattern = patterns.find(
                (candidate) => candidate.patternId === event.target.value
              )
              onUpdateForm({
                ...form,
                patternId: event.target.value,
                name: pattern ? trackingPatternLabel(pattern) : form.name,
              })
            }}
            data={[
              { value: "", label: "No GA4 event" },
              ...patterns.map((pattern) => ({
                value: pattern.patternId,
                label: trackingPatternLabel(pattern),
              })),
            ]}
          />
          <TextInput
            label="Name"
            required
            value={form.name}
            onChange={(event) => onUpdateForm({ ...form, name: event.target.value })}
          />
          <TextInput
            label="GA4 event"
            required
            value={form.ga4EventName}
            onChange={(event) => onUpdateForm({ ...form, ga4EventName: event.target.value })}
          />
          <TextInput
            label="Meta event"
            required
            value={form.metaEventName}
            onChange={(event) => onUpdateForm({ ...form, metaEventName: event.target.value })}
          />
        </SimpleGrid>
        <FormActions>
          <Button disabled={saving || !form.name.trim()} type="submit">
            {saving ? "Adding..." : "Add mapping"}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}

function TrackingRealtimeSummary({ summary }: { summary: RealtimeTrackingEventsPayload | null }) {
  if (!summary) {
    return <EmptyState>No realtime check has run yet.</EmptyState>
  }
  if (summary.events.length === 0) {
    return <EmptyState>No Adgiro events found in the last {summary.minutes} minutes.</EmptyState>
  }
  const semanticEvents = summary.events.filter((event) =>
    ["adgiro_lead", "adgiro_signup", "adgiro_purchase"].includes(event.eventName)
  )
  return (
    <>
      {semanticEvents.length > 0 ? (
        <Group gap="xs" mb="xs">
          <StatusBadge color="green">Ready</StatusBadge>
          <Text size="sm">
            Manual conversion event detected:{" "}
            {semanticEvents.map((event) => event.eventName).join(", ")}
          </Text>
        </Group>
      ) : null}
      <DataTableShell minWidth={560} withColumnBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Realtime event</Table.Th>
            <Table.Th>Count</Table.Th>
            <Table.Th>Checked</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {summary.events.map((event) => (
            <Table.Tr key={event.eventName}>
              <Table.Td>{event.eventName}</Table.Td>
              <Table.Td>{event.count}</Table.Td>
              <Table.Td>{formatDateTime(summary.generatedAt)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DataTableShell>
    </>
  )
}

function TrackingMappingsTable({
  mappings,
  saving,
  onUpdateStatus,
}: {
  mappings: SiteEventMapping[]
  saving: boolean
  onUpdateStatus: (mappingId: string, status: SiteEventMapping["status"]) => void
}) {
  if (mappings.length === 0) {
    return <EmptyState>No tracking mappings yet.</EmptyState>
  }
  return (
    <DataTableShell minWidth={760}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>GA4</Table.Th>
          <Table.Th>Meta</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th aria-label="Actions" />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {mappings.map((mapping) => (
          <Table.Tr key={mapping.mappingId}>
            <Table.Td>
              {mapping.name}
              <small>{mapping.rationale}</small>
            </Table.Td>
            <Table.Td>{mapping.ga4EventName}</Table.Td>
            <Table.Td>{mapping.metaEventName}</Table.Td>
            <Table.Td>{mapping.status}</Table.Td>
            <Table.Td className="data-table__actions">
              {mapping.status === "disabled" ? (
                <Button
                  size="xs"
                  disabled={saving}
                  type="button"
                  onClick={() => onUpdateStatus(mapping.mappingId, "active")}
                >
                  Enable
                </Button>
              ) : (
                <Button
                  variant="subtle"
                  size="xs"
                  disabled={saving}
                  type="button"
                  onClick={() => onUpdateStatus(mapping.mappingId, "disabled")}
                >
                  Disable
                </Button>
              )}
              <ActionIcon
                color="red"
                variant="subtle"
                disabled={saving}
                aria-label={`Delete ${mapping.name}`}
                onClick={() => onUpdateStatus(mapping.mappingId, "deleted")}
              >
                ×
              </ActionIcon>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </DataTableShell>
  )
}

function ConversionValidationCard({
  validation,
}: {
  validation: ConversionValidationSummary | null
}) {
  if (!validation) return null
  return (
    <AppCard className="conversion-validation-card" p="md">
      <div className="conversion-validation-card__header">
        <div>
          <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
            Conversion delivery
          </Text>
          <Text size="sm">Last validation started {formatDateTime(validation.run.createdAt)}</Text>
        </div>
        <StatusBadge color={conversionValidationStatusColor(validation.run.status)}>
          {conversionValidationStatusLabel(validation.run.status)}
        </StatusBadge>
      </div>
      {validation.results.length > 0 ? (
        <div className="conversion-validation-results">
          {validation.results.map((result) => (
            <div className="conversion-validation-result" key={result.validationResultId}>
              <div>
                <strong>{conversionValidationProviderLabel(result.provider)}</strong>
                <small>{result.message}</small>
              </div>
              <StatusBadge color={conversionValidationStatusColor(result.status)}>
                {conversionValidationStatusLabel(result.status)}
              </StatusBadge>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState>Validation is queued. Results will appear after the job starts.</EmptyState>
      )}
    </AppCard>
  )
}

function preferredRealtimeValidationMapping(
  mappings: SiteEventMapping[],
  realtimeSummary: RealtimeTrackingEventsPayload | null
): SiteEventMapping | null {
  const eventNames = new Set((realtimeSummary?.events ?? []).map((event) => event.eventName))
  if (eventNames.has("adgiro_purchase")) {
    return mappings.find((mapping) => mapping.conversionEventName === "purchase") ?? null
  }
  if (eventNames.has("adgiro_signup")) {
    return mappings.find((mapping) => mapping.conversionEventName === "sign_up") ?? null
  }
  if (eventNames.has("adgiro_lead")) {
    return mappings.find((mapping) => mapping.conversionEventName === "generate_lead") ?? null
  }
  return null
}

function compareValidationMappings(left: SiteEventMapping, right: SiteEventMapping): number {
  return (
    validationMappingRank(left) - validationMappingRank(right) ||
    left.name.localeCompare(right.name) ||
    left.mappingId.localeCompare(right.mappingId)
  )
}

function validationMappingRank(mapping: SiteEventMapping): number {
  if (mapping.source === "system" && !isJourneyMapping(mapping)) return 0
  if (!isJourneyMapping(mapping)) return 1
  return 2
}

function validationMappingLabel(mapping: SiteEventMapping): string {
  return isJourneyMapping(mapping) ? `${mapping.name} (journey)` : mapping.name
}

function isJourneyMapping(mapping: SiteEventMapping): boolean {
  const match = mapping.valueJson?.match
  return Boolean(match && typeof match === "object" && "type" in match && match.type === "journey")
}

function TrackingEventSummariesTable({
  summaries,
  saving,
  onCreateMapping,
}: {
  summaries: CapturedEventSummary[]
  saving: boolean
  onCreateMapping: (summary: CapturedEventSummary) => void
}) {
  if (summaries.length === 0) {
    return <EmptyState>No captured event summaries yet.</EmptyState>
  }
  return (
    <DataTableShell minWidth={980}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Captured event</Table.Th>
          <Table.Th>Intent</Table.Th>
          <Table.Th>Lifecycle</Table.Th>
          <Table.Th>Page</Table.Th>
          <Table.Th>Signals</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Last seen</Table.Th>
          <Table.Th>Action</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {summaries.slice(0, 12).map((summary) => (
          <Table.Tr key={summary.summaryId}>
            <Table.Td>
              {summary.label}
              <small>{summary.rationale}</small>
            </Table.Td>
            <Table.Td>{eventIntentLabel(summary.intent)}</Table.Td>
            <Table.Td>{eventLifecycleLabel(summary.accountLifecycle)}</Table.Td>
            <Table.Td>{summary.pageUrl ?? "Any page"}</Table.Td>
            <Table.Td>
              {summary.totalCount}
              <small>{summary.eventNames.join(", ")}</small>
            </Table.Td>
            <Table.Td>{summary.status}</Table.Td>
            <Table.Td>{formatDateTime(summary.lastSeenAt)}</Table.Td>
            <Table.Td>
              {summary.status === "unmapped" && summary.optimizationCandidate ? (
                <Button
                  size="xs"
                  variant="subtle"
                  disabled={saving}
                  type="button"
                  onClick={() => onCreateMapping(summary)}
                >
                  Add event mapping
                </Button>
              ) : (
                <Text c="dimmed" size="xs">
                  {summary.status === "mapped" ? "Mapped" : "No action"}
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </DataTableShell>
  )
}

function TrackingJourneySummariesTable({
  summaries,
  saving,
  mappedJourneySummaryIds,
  onCreateMapping,
}: {
  summaries: CapturedJourneySummary[]
  saving: boolean
  mappedJourneySummaryIds: Set<string>
  onCreateMapping: (summary: CapturedJourneySummary) => void
}) {
  if (summaries.length === 0) {
    return <EmptyState>No captured journey summaries yet.</EmptyState>
  }
  return (
    <DataTableShell minWidth={980}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Journey</Table.Th>
          <Table.Th>Intent</Table.Th>
          <Table.Th>Lifecycle</Table.Th>
          <Table.Th ta="right">Sess.</Table.Th>
          <Table.Th ta="right">Conf.</Table.Th>
          <Table.Th>Sequence</Table.Th>
          <Table.Th>Suggested</Table.Th>
          <Table.Th>Last seen</Table.Th>
          <Table.Th>Action</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {summaries.slice(0, 12).map((summary) => {
          const mapped = mappedJourneySummaryIds.has(summary.journeySummaryId)
          return (
            <Table.Tr key={summary.journeySummaryId}>
              <Table.Td>
                {summary.label}
                <small>{summary.rationale}</small>
              </Table.Td>
              <Table.Td>{journeyIntentLabel(summary.intent)}</Table.Td>
              <Table.Td>{eventLifecycleLabel(summary.accountLifecycle)}</Table.Td>
              <Table.Td ta="right">{summary.sessionCount}</Table.Td>
              <Table.Td ta="right">{Math.round(summary.confidence * 100)}%</Table.Td>
              <Table.Td>
                {summary.sampleSequence
                  .map((step) => step.elementText || step.pageUrl || step.eventName)
                  .slice(0, 5)
                  .join(" -> ")}
              </Table.Td>
              <Table.Td>{summary.suggestedGa4EventName ?? "None"}</Table.Td>
              <Table.Td>{formatDateTime(summary.lastSeenAt)}</Table.Td>
              <Table.Td>
                {!mapped && summary.optimizationCandidate ? (
                  <Button
                    size="xs"
                    variant="subtle"
                    disabled={saving}
                    type="button"
                    onClick={() => onCreateMapping(summary)}
                  >
                    Add journey mapping
                  </Button>
                ) : (
                  <Text c="dimmed" size="xs">
                    {mapped ? "Mapped" : "No action"}
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </DataTableShell>
  )
}

function journeyIntentLabel(intent: CapturedJourneySummary["intent"]): string {
  switch (intent) {
    case "lead":
      return "Lead"
    case "signup":
      return "Signup"
    case "account":
      return "Account"
    case "engagement":
      return "Engagement"
  }
}

function eventLifecycleLabel(lifecycle: CapturedEventSummary["accountLifecycle"]): string {
  switch (lifecycle) {
    case "new":
      return "New"
    case "existing":
      return "Existing"
    case "unknown":
      return "Unknown"
    default:
      return "Unknown"
  }
}

function eventIntentLabel(intent: CapturedEventSummary["intent"]): string {
  switch (intent) {
    case "lead":
      return "Lead"
    case "purchase":
      return "Purchase"
    case "signup":
      return "Signup"
    case "contact":
      return "Contact"
    case "account":
      return "Account"
    case "navigation":
      return "Navigation"
    case "engagement":
      return "Engagement"
  }
}

function defaultConversionEventName(
  intent: CapturedEventSummary["intent"] | CapturedJourneySummary["intent"]
): string {
  if (intent === "purchase") return "purchase"
  if (intent === "signup" || intent === "account") return "sign_up"
  return "generate_lead"
}

function defaultGa4EventName(
  intent: CapturedEventSummary["intent"] | CapturedJourneySummary["intent"]
): string {
  return defaultConversionEventName(intent)
}

function defaultMetaEventName(
  intent: CapturedEventSummary["intent"] | CapturedJourneySummary["intent"]
): string {
  if (intent === "purchase") return "Purchase"
  if (intent === "signup" || intent === "account") return "CompleteRegistration"
  return "Lead"
}

function journeyMappingValue(summary: CapturedJourneySummary): Record<string, unknown> {
  return {
    match: {
      type: "journey",
      journeySummaryId: summary.journeySummaryId,
      minScore: summary.intent === "lead" ? 70 : 60,
      pendingIntent: summary.intent === "lead" ? ["lead", "signup"] : [summary.intent],
      onboardingSeen: summary.accountLifecycle === "new" || summary.intent === "lead",
      accountLifecycle: summary.accountLifecycle,
    },
  }
}

type AssetTableRow =
  | { type: "asset"; asset: SiteAssetRecord }
  | { type: "group"; key: string; label: string; assets: SiteAssetRecord[] }

interface GeneratedImageInfo {
  groupId: string
  conceptId: string
  variant: string | null
}

function groupedAssetRows(assets: SiteAssetRecord[]): AssetTableRow[] {
  const rows: AssetTableRow[] = []
  const grouped = new Map<string, { label: string; assets: SiteAssetRecord[] }>()

  for (const asset of assets) {
    const info = generatedImageInfo(asset)
    if (!info) {
      rows.push({ type: "asset", asset })
      continue
    }

    const key = `${info.groupId}:${info.conceptId}`
    const existing = grouped.get(key)
    if (existing) {
      existing.assets.push(asset)
    } else {
      grouped.set(key, {
        label: conceptLabel(asset, info.conceptId),
        assets: [asset],
      })
    }
  }

  for (const [key, group] of grouped) {
    rows.push({ type: "group", key, label: group.label, assets: group.assets })
    for (const asset of group.assets) {
      rows.push({ type: "asset", asset })
    }
  }

  return rows
}

function generatedImageInfo(asset: SiteAssetRecord): GeneratedImageInfo | null {
  if (asset.assetType !== "image" || !asset.valueJson) return null
  const groupId = typeof asset.valueJson.groupId === "string" ? asset.valueJson.groupId : null
  const conceptId = typeof asset.valueJson.conceptId === "string" ? asset.valueJson.conceptId : null
  const variant = typeof asset.valueJson.variant === "string" ? asset.valueJson.variant : null
  return groupId && conceptId ? { groupId, conceptId, variant } : null
}

function conceptLabel(asset: SiteAssetRecord, conceptId: string): string {
  const strippedName = asset.name.replace(/\s+\((Square|Vertical|Horizontal)\)$/i, "").trim()
  return strippedName || conceptId
}

function variantSummary(assets: SiteAssetRecord[]): string {
  return assets
    .map((asset) => generatedImageInfo(asset)?.variant)
    .filter((variant): variant is string => Boolean(variant))
    .map(imageVariantLabel)
    .join(", ")
}

function sourceSummary(assets: SiteAssetRecord[]): string {
  const sources = new Set(assets.map((asset) => asset.source))
  return Array.from(sources).join(", ")
}

function overviewCategorySummary(assets: SiteAssetRecord[], sourceCount: number): string {
  if (assets.length === 0) return "No assets yet."
  const sampleNames = assets
    .slice(0, 2)
    .map((asset) => asset.name || assetDisplayValue(asset))
    .filter(Boolean)
  const sourceLabel =
    sourceCount === 1
      ? `${assets[0]?.source ?? "unknown"} source`
      : `${String(sourceCount)} sources`
  return `${sourceLabel}${sampleNames.length > 0 ? ` · ${sampleNames.join(", ")}` : ""}`
}

function trackingPatternLabel(pattern: ObservedEventPattern): string {
  return [pattern.elementText, pattern.eventName, pattern.selectorHint].filter(Boolean).join(" · ")
}

function SiteSetupPanel({
  crawlState,
  providerLinks,
  selectedMeasurementParentAccountId,
  tracking,
  linkedRoles,
  linkingAccount,
  linkingSearchConsole,
  creatingMeasurement,
  loadingCrawl,
  recrawlingSite,
  searchConsoleModalOpen,
  searchConsoleSetup,
  stoppingCrawl,
  onAutoLink,
  onCloseSearchConsoleSetup,
  onCreateMeasurement,
  onLink,
  onRefreshCrawl,
  onRecrawl,
  onRefreshMetaPages,
  onSelectMeasurementParentAccount,
  onSelectMetaPage,
  onStartSearchConsoleSetup,
  onStopCrawl,
  onUnlink,
  onVerifySearchConsoleSetup,
}: {
  crawlState: SiteCrawlState | null
  providerLinks: SiteProviderLinksPayload
  tracking: SiteTrackingPayload | null
  linkedRoles: Set<SiteProviderRole>
  linkingAccount: boolean
  linkingSearchConsole: boolean
  creatingMeasurement: boolean
  loadingCrawl: boolean
  recrawlingSite: boolean
  searchConsoleModalOpen: boolean
  searchConsoleSetup: SearchConsoleSetupPayload | null
  stoppingCrawl: boolean
  selectedMeasurementParentAccountId: string
  onAutoLink: () => void
  onCloseSearchConsoleSetup: () => void
  onCreateMeasurement: () => void
  onLink: (role: SiteProviderRole, providerAccountId: string) => void
  onRefreshCrawl: () => void
  onRecrawl: () => void
  onRefreshMetaPages: (siteProviderLinkId: string) => void
  onSelectMeasurementParentAccount: (providerAccountId: string) => void
  onSelectMetaPage: (siteProviderLinkId: string, pageId: string) => void
  onStartSearchConsoleSetup: () => void
  onStopCrawl: () => void
  onUnlink: (siteProviderLinkId: string) => void
  onVerifySearchConsoleSetup: () => void
}) {
  const analyticsLinks = providerLinks.links.filter((link) => link.role === "analytics")
  const analyticsLink = analyticsLinks[0]
  const googleConnections = new Map(
    providerLinks.candidates
      .filter((candidate) => candidate.provider === "google")
      .map((candidate) => [candidate.providerConnectionId, candidate])
  )
  const measurementParentAccounts = providerLinks.measurementParentAccounts
  const canCreateMeasurement = googleConnections.size > 0 || measurementParentAccounts.length > 0
  const selectedMeasurementParentAccount = measurementParentAccounts.find(
    (account) => account.providerAccountId === selectedMeasurementParentAccountId
  )
  const metaAdsLink = providerLinks.links.find(
    (link) => link.role === "ads" && link.provider === "meta"
  )
  const setupSteps = [
    {
      label: "Crawl",
      ready: Boolean(crawlState?.intelligence?.status === "ready" || crawlState?.pages.length),
      message: crawlState?.intelligence?.status === "ready" ? "Indexed" : "Needs crawl",
    },
    {
      label: "Ads",
      ready: linkedRoles.has("ads"),
      message: linkedRoles.has("ads") ? "Linked" : "Link Google or Meta",
    },
    ...(metaAdsLink
      ? [
          {
            label: "Meta Page",
            ready: Boolean(metaSelectedPageId(metaAdsLink.account.metadata)),
            message: metaSelectedPageId(metaAdsLink.account.metadata) ? "Selected" : "Choose Page",
          },
        ]
      : []),
    {
      label: "Tracking",
      ready: Boolean(tracking?.config.ga4MeasurementId),
      message: tracking?.config.ga4MeasurementId ?? "Not configured",
    },
    {
      label: "Search Console",
      ready: linkedRoles.has("search_console"),
      message: linkedRoles.has("search_console") ? "Linked" : "Optional",
    },
  ]

  return (
    <>
      <AppCard className="site-setup-card">
        <section className="site-setup-section site-setup-section--readiness">
          <SectionHeader
            title="Setup readiness"
            description="The critical provider and tracking pieces for campaign launch."
          />
          <div className="setup-readiness-strip">
            {setupSteps.map((step) => (
              <div
                className={`setup-readiness-step${step.ready ? " setup-readiness-step--ready" : ""}`}
                key={step.label}
              >
                <StatusBadge color={step.ready ? "green" : "gray"}>
                  {step.ready ? "Ready" : "Needed"}
                </StatusBadge>
                <strong>{step.label}</strong>
                <span>{step.message}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="site-setup-section">
          <SectionHeader
            title="Connected accounts"
            description="Link ads, analytics, and Search Console accounts for this site."
            actions={
              <Button size="xs" disabled={linkingAccount} onClick={onAutoLink}>
                Auto-link matches
              </Button>
            }
          />
          <div className="site-provider-links">
            <div className="site-provider-role-strip">
              {siteProviderRoles.map((role) => (
                <div className="site-provider-role-chip" key={role}>
                  <span>{providerRoleLabel(role)}</span>
                  <StatusBadge color={linkedRoles.has(role) ? "green" : "gray"}>
                    {linkedRoles.has(role) ? "Linked" : "Not linked"}
                  </StatusBadge>
                </div>
              ))}
            </div>
            {siteProviderRoles.map((role) => {
              const links = providerLinks.links.filter((link) => link.role === role)
              const candidates = providerLinks.candidates.filter(
                (candidate) => candidate.role === role && !candidate.linked
              )
              const recommended = links.length === 0 ? bestRecommendedCandidate(candidates) : null
              const isAnalytics = role === "analytics"
              const sortedLinks = [...links].sort((left, right) => {
                const providerOrder = labelForProvider(left.provider).localeCompare(
                  labelForProvider(right.provider)
                )
                if (providerOrder !== 0) {
                  return providerOrder
                }
                return providerAccountDisplayName(left.account).localeCompare(
                  providerAccountDisplayName(right.account)
                )
              })

              return (
                <div className="site-provider-link-group" key={role}>
                  <div className="site-provider-link-group__heading">
                    <div>
                      <strong>{providerRoleLabel(role)}</strong>
                      <small>{providerRoleSetupHint(role)}</small>
                    </div>
                    <AccountLinkMenu
                      candidates={candidates}
                      disabled={linkingAccount}
                      linked={links.length > 0}
                      secondary={Boolean(recommended)}
                      roleLabel={providerRoleLabel(role)}
                      onLink={(providerAccountId) => onLink(role, providerAccountId)}
                    />
                    {role === "search_console" && links.length === 0 ? (
                      <Button
                        size="xs"
                        type="button"
                        variant="light"
                        disabled={linkingSearchConsole}
                        onClick={onStartSearchConsoleSetup}
                      >
                        {linkingSearchConsole ? "Preparing..." : "Verify domain"}
                      </Button>
                    ) : null}
                    {isAnalytics ? (
                      <Group className="site-provider-measurement-actions" gap={6} wrap="wrap">
                        <MeasurementParentMenu
                          accounts={measurementParentAccounts}
                          disabled={creatingMeasurement}
                          selectedAccount={selectedMeasurementParentAccount}
                          onSelect={onSelectMeasurementParentAccount}
                        />
                        <Button
                          size="xs"
                          disabled={
                            creatingMeasurement ||
                            !canCreateMeasurement ||
                            (measurementParentAccounts.length !== 1 &&
                              !selectedMeasurementParentAccountId)
                          }
                          type="button"
                          onClick={onCreateMeasurement}
                        >
                          {creatingMeasurement
                            ? "Creating..."
                            : analyticsLinks.length > 0 || tracking?.config.ga4MeasurementId
                              ? "Replace Adgiro measurement"
                              : "Create Adgiro measurement"}
                        </Button>
                      </Group>
                    ) : null}
                  </div>
                  {isAnalytics ? (
                    <>
                      <div className="site-readiness-grid site-readiness-grid--inline">
                        <div className="site-readiness-item">
                          <span>Status</span>
                          <strong>{tracking?.config.ga4MeasurementId ? "Ready" : "Not set"}</strong>
                        </div>
                        <div className="site-readiness-item">
                          <span>Measurement ID</span>
                          <strong>{tracking?.config.ga4MeasurementId ?? "None"}</strong>
                        </div>
                        <div className="site-readiness-item site-readiness-item--wide">
                          <span>Property</span>
                          <strong>
                            {analyticsLink
                              ? providerAccountDisplayName(analyticsLink.account)
                              : "Not linked"}
                          </strong>
                        </div>
                      </div>
                      {analyticsLinks.length > 1 ? (
                        <Text c="orange" size="xs">
                          Multiple GA4 properties are linked. Keep one measurement property linked.
                        </Text>
                      ) : null}
                    </>
                  ) : null}
                  {links.length > 0 ? (
                    <div className="site-provider-link-list">
                      {sortedLinks.map((link, index) => (
                        <div
                          className={`site-provider-link-row${
                            role === "ads" &&
                            index > 0 &&
                            sortedLinks[index - 1]?.provider !== link.provider
                              ? " site-provider-link-row--provider-divider"
                              : ""
                          }`}
                          key={link.siteProviderLinkId}
                        >
                          <span className="site-provider-link-account">
                            <strong>{providerAccountDisplayName(link.account)}</strong>
                            <small>{link.account.externalAccountId}</small>
                            {link.provider === "meta" && link.role === "ads" ? (
                              <small>{metaPageSetupLabel(link.account.metadata)}</small>
                            ) : null}
                          </span>
                          <span>{formatAccountType(link.account.accountType)}</span>
                          <span>{labelForProvider(link.provider)}</span>
                          <Group className="site-provider-link-actions" gap={4} wrap="nowrap">
                            {link.provider === "meta" && link.role === "ads" ? (
                              <>
                                <NativeSelect
                                  aria-label="Meta Page"
                                  disabled={
                                    linkingAccount ||
                                    metaPageOptions(link.account.metadata).length === 0
                                  }
                                  value={metaSelectedPageId(link.account.metadata)}
                                  onChange={(event) =>
                                    onSelectMetaPage(link.siteProviderLinkId, event.target.value)
                                  }
                                  data={[
                                    {
                                      value: "",
                                      label:
                                        metaPageOptions(link.account.metadata).length === 0
                                          ? "No Pages available"
                                          : "Choose Page",
                                    },
                                    ...metaPageOptions(link.account.metadata),
                                  ]}
                                />
                                <ActionIcon
                                  aria-label="Refresh Meta Pages"
                                  disabled={linkingAccount}
                                  title="Refresh Meta Pages"
                                  variant="subtle"
                                  onClick={() => onRefreshMetaPages(link.siteProviderLinkId)}
                                >
                                  ↻
                                </ActionIcon>
                                {metaSelectedPageId(link.account.metadata) ? null : (
                                  <ActionIcon
                                    aria-label="How to create a Facebook Page"
                                    component="a"
                                    href="https://www.facebook.com/help/104002523024878"
                                    rel="noreferrer"
                                    target="_blank"
                                    title="How to create a Facebook Page"
                                    variant="subtle"
                                  >
                                    ?
                                  </ActionIcon>
                                )}
                              </>
                            ) : null}
                            <ActionIcon
                              aria-label={`Unlink ${link.account.label ?? link.account.externalAccountId}`}
                              color="red"
                              variant="subtle"
                              disabled={linkingAccount}
                              title="Unlink account"
                              onClick={() => onUnlink(link.siteProviderLinkId)}
                            >
                              <TrashIcon />
                            </ActionIcon>
                          </Group>
                        </div>
                      ))}
                    </div>
                  ) : recommended ? (
                    <ProviderRecommendation
                      candidate={recommended}
                      disabled={linkingAccount}
                      onUse={(providerAccountId) => onLink(role, providerAccountId)}
                    />
                  ) : (
                    <EmptyState>No linked account.</EmptyState>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      </AppCard>
      <SearchConsoleSetupModal
        opened={searchConsoleModalOpen}
        setup={searchConsoleSetup}
        verifying={linkingSearchConsole}
        onClose={onCloseSearchConsoleSetup}
        onVerify={onVerifySearchConsoleSetup}
      />
      <CrawlStatusPanel
        crawlState={crawlState}
        loading={loadingCrawl}
        recrawling={recrawlingSite}
        stopping={stoppingCrawl}
        onRefresh={onRefreshCrawl}
        onRecrawl={onRecrawl}
        onStop={onStopCrawl}
      />
    </>
  )
}

function providerRoleSetupHint(role: SiteProviderRole): string {
  switch (role) {
    case "ads":
      return "Used for Google, PMax, and Meta launches."
    case "analytics":
      return "Used for measurement and conversion feedback."
    case "search_console":
      return "Used for organic query and landing-page context."
  }
}

function SearchConsoleSetupModal({
  opened,
  setup,
  verifying,
  onClose,
  onVerify,
}: {
  opened: boolean
  setup: SearchConsoleSetupPayload | null
  verifying: boolean
  onClose: () => void
  onVerify: () => void
}) {
  return (
    <Modal opened={opened} title="Link Search Console" size="lg" onClose={onClose}>
      {setup ? (
        <div className="search-console-setup">
          <div className="site-readiness-grid site-readiness-grid--inline">
            <div className="site-readiness-item">
              <span>Domain</span>
              <strong>{setup.domain}</strong>
            </div>
            <div className="site-readiness-item">
              <span>DNS</span>
              <strong>{setup.nameserversAreCloudflare ? "Cloudflare" : "Manual"}</strong>
            </div>
          </div>
          {setup.status === "reconnect_required" ? (
            <>
              <Text c="dimmed" size="sm">
                Reconnect Google to grant domain verification access, then return here to add the
                Search Console TXT record.
              </Text>
              {setup.reconnectUrl ? (
                <Button component="a" href={setup.reconnectUrl}>
                  Reconnect Google
                </Button>
              ) : null}
            </>
          ) : null}
          {setup.cloudflareInstall.available && setup.cloudflareInstall.url ? (
            <Button
              component="a"
              href={setup.cloudflareInstall.url}
              target="_blank"
              rel="noreferrer"
            >
              Add TXT with Cloudflare
            </Button>
          ) : null}
          {setup.dnsRecord ? (
            <>
              <div className="conversion-validation-modal__mapping">
                <span>
                  <strong>Type:</strong> {setup.dnsRecord.type}
                </span>
                <span>
                  <strong>Host:</strong> {setup.dnsRecord.host}
                </span>
              </div>
              <Code block className="conversion-validation-modal__code">
                {setup.dnsRecord.value}
              </Code>
            </>
          ) : null}
          <Group justify="flex-end" mt="md">
            <Button variant="light" type="button" onClick={onClose}>
              Close
            </Button>
            <Button disabled={verifying || !setup.dnsRecord} type="button" onClick={onVerify}>
              {verifying ? "Checking..." : "Verify now"}
            </Button>
          </Group>
        </div>
      ) : (
        <Text c="dimmed" size="sm">
          Preparing Search Console verification.
        </Text>
      )}
    </Modal>
  )
}

function AccountLinkMenu({
  candidates,
  disabled,
  linked,
  secondary,
  roleLabel,
  onLink,
}: {
  candidates: SiteProviderCandidateRecord[]
  disabled: boolean
  linked: boolean
  secondary?: boolean
  roleLabel: string
  onLink: (providerAccountId: string) => void
}) {
  const label = linked ? "Re-link account" : secondary ? "Use a different account" : "Link account"
  return (
    <Menu position="bottom-end" shadow="md" width={320} withinPortal>
      <Menu.Target>
        <Button
          size="xs"
          type="button"
          variant={linked || secondary ? "light" : "filled"}
          disabled={disabled || candidates.length === 0}
        >
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{roleLabel}</Menu.Label>
        {candidates.length === 0 ? (
          <Menu.Item disabled>No accounts available</Menu.Item>
        ) : (
          candidates.map((candidate) => (
            <Menu.Item
              key={candidate.providerAccountId}
              onClick={() => onLink(candidate.providerAccountId)}
            >
              {candidateOptionLabel(candidate)}
            </Menu.Item>
          ))
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

function bestRecommendedCandidate(
  candidates: SiteProviderCandidateRecord[]
): SiteProviderCandidateRecord | null {
  // Only surface a match confident enough to recommend; weaker ones stay behind manual selection.
  const ranked = candidates
    .filter((candidate) => candidate.matchRank >= 70)
    .sort((left, right) => right.matchRank - left.matchRank)
  return ranked[0] ?? null
}

function ProviderRecommendation({
  candidate,
  disabled,
  onUse,
}: {
  candidate: SiteProviderCandidateRecord
  disabled: boolean
  onUse: (providerAccountId: string) => void
}) {
  const strong = candidate.matchRank >= 100
  return (
    <div className="provider-recommendation">
      <div className="provider-recommendation__body">
        <StatusBadge color={strong ? "green" : "blue"}>
          {strong ? "Strong match" : "Likely match"}
        </StatusBadge>
        <div className="provider-recommendation__detail">
          <strong>{providerAccountDisplayName(candidate)}</strong>
          <small>
            {labelForProvider(candidate.provider)} {formatAccountType(candidate.accountType)} ·{" "}
            {candidate.externalAccountId}
          </small>
          {candidate.matchReasons.length > 0 ? (
            <small className="provider-recommendation__reasons">
              {candidate.matchReasons.join(" · ")}
            </small>
          ) : null}
        </div>
      </div>
      <Button
        size="xs"
        type="button"
        disabled={disabled}
        onClick={() => onUse(candidate.providerAccountId)}
      >
        Use recommended
      </Button>
    </div>
  )
}

function MeasurementParentMenu({
  accounts,
  disabled,
  selectedAccount,
  onSelect,
}: {
  accounts: ProviderAccountRecord[]
  disabled: boolean
  selectedAccount: ProviderAccountRecord | undefined
  onSelect: (providerAccountId: string) => void
}) {
  return (
    <Menu position="bottom-end" shadow="md" width={320} withinPortal>
      <Menu.Target>
        <Button
          size="xs"
          type="button"
          variant={selectedAccount ? "light" : "default"}
          disabled={disabled || accounts.length === 0}
        >
          {selectedAccount ? providerAccountDisplayName(selectedAccount) : "Choose GA4 parent"}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>GA4 parent account</Menu.Label>
        {accounts.length === 0 ? (
          <Menu.Item disabled>No GA4 parent account</Menu.Item>
        ) : (
          accounts.map((account) => (
            <Menu.Item
              key={account.providerAccountId}
              onClick={() => onSelect(account.providerAccountId)}
            >
              {providerAccountDisplayName(account)}
            </Menu.Item>
          ))
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="14"
      viewBox="0 0 24 24"
      width="14"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 3h6m-8 5h10m-9 0 .7 12h6.6L16 8M10 11v6m4-6v6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

function GrowthAutomationPanel({
  form,
  activityTree,
  jobStatusPanel,
  saving,
  onSubmit,
  onUpdate,
}: {
  form: GrowthAutomationFormState
  activityTree: JobTreeRecord | null
  jobStatusPanel: ReactNode
  saving: boolean
  onSubmit: (event: FormEvent) => void
  onUpdate: (form: GrowthAutomationFormState) => void
}) {
  return (
    <AppCard className="growth-automation-panel">
      <form className="stack" onSubmit={onSubmit}>
        <SectionHeader
          title="Growth automation"
          description="Let the optimizer run apply-mode actions for this site from the hourly scheduler."
          actions={
            <Button type="submit" loading={saving}>
              Save
            </Button>
          }
        />
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
          <NativeSelect
            label="Mode"
            value={form.automationMode}
            data={[
              { value: "manual", label: "Manual" },
              { value: "assist", label: "Assist" },
              { value: "full_auto", label: "Full auto" },
            ]}
            onChange={(event) =>
              onUpdate({
                ...form,
                automationMode: event.currentTarget.value as SiteAutomationMode,
              })
            }
          />
          <TextInput
            label="Daily cap"
            inputMode="decimal"
            value={form.dailyBudgetCap}
            placeholder="50.00"
            onChange={(event) => onUpdate({ ...form, dailyBudgetCap: event.currentTarget.value })}
          />
          <TextInput
            label="Experiment cap"
            inputMode="decimal"
            value={form.experimentBudgetCap}
            placeholder="50.00"
            onChange={(event) =>
              onUpdate({ ...form, experimentBudgetCap: event.currentTarget.value })
            }
          />
          <TextInput
            label="Target CPA"
            inputMode="decimal"
            value={form.targetCostPerPrimaryConversion}
            placeholder="4.00"
            onChange={(event) =>
              onUpdate({
                ...form,
                targetCostPerPrimaryConversion: event.currentTarget.value,
              })
            }
          />
          <TextInput
            label="Currency"
            value={form.currency}
            placeholder="USD"
            onChange={(event) =>
              onUpdate({ ...form, currency: event.currentTarget.value.toUpperCase() })
            }
          />
        </SimpleGrid>
        {jobStatusPanel}
        <FullAutoActivityFeed tree={activityTree} />
      </form>
    </AppCard>
  )
}

function growthAutomationFormFromSite(site: SiteRecord): GrowthAutomationFormState {
  return {
    automationMode: site.automationMode,
    dailyBudgetCap: site.growthSettings?.dailyBudgetCap ?? "",
    experimentBudgetCap: site.growthSettings?.experimentBudgetCap ?? "",
    targetCostPerPrimaryConversion: site.growthSettings?.targetCostPerPrimaryConversion ?? "",
    currency: site.growthSettings?.currency ?? "USD",
  }
}

function growthAutomationRequest(form: GrowthAutomationFormState): {
  automationMode: SiteAutomationMode
  growthSettings: SiteGrowthSettings
} {
  return {
    automationMode: form.automationMode,
    growthSettings: {
      dailyBudgetCap: cleanOptionalFormValue(form.dailyBudgetCap),
      experimentBudgetCap: cleanOptionalFormValue(form.experimentBudgetCap),
      targetCostPerPrimaryConversion: cleanOptionalFormValue(form.targetCostPerPrimaryConversion),
      currency: cleanOptionalFormValue(form.currency.toUpperCase()) ?? "USD",
    },
  }
}

function cleanOptionalFormValue(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function PlaceholderPanel({ title, status }: { title: string; status: string }) {
  return (
    <section className="dashboard-card">
      <h2>{title}</h2>
      <p>{status}</p>
    </section>
  )
}

type CockpitTone = "live" | "ready" | "progress" | "attention" | "idle"

interface CockpitAction {
  label: string
  onClick: () => void
  variant?: "filled" | "light"
}

interface CockpitArea {
  key: string
  label: string
  statusLabel: string
  tone: CockpitTone
  summary: string
  recommendation: string | null
  primary: CockpitAction
}

function cockpitToneColor(tone: CockpitTone): string {
  switch (tone) {
    case "live":
      return "teal"
    case "ready":
      return "green"
    case "progress":
      return "blue"
    case "attention":
      return "orange"
    case "idle":
      return "gray"
  }
}

function cockpitMoney(micros: number): string {
  return new Intl.NumberFormat("en-US", { currency: "USD", style: "currency" }).format(
    micros / 1_000_000
  )
}

function bestChannelReadiness(
  generation: CampaignGenerationState | null,
  channels: CampaignChannelKey[]
): { ready: boolean; score: number; topMissing: string | null } {
  if (!generation) return { ready: false, score: 0, topMissing: null }
  let ready = false
  let best: { score: number; ready: boolean; missing: string[] } | null = null
  for (const segment of generation.segments) {
    for (const channelReadiness of segment.readiness.channelReadiness) {
      if (!channels.includes(channelReadiness.channel)) continue
      if (channelReadiness.ready) ready = true
      if (!best || channelReadiness.score > best.score) {
        best = {
          score: channelReadiness.score,
          ready: channelReadiness.ready,
          missing: channelReadiness.missing,
        }
      }
    }
  }
  return {
    ready,
    score: best?.score ?? 0,
    topMissing: best && !best.ready ? (best.missing[0] ?? null) : null,
  }
}

function linkedAdsAccountName(
  providerLinks: SiteProviderLinksPayload,
  provider: string
): string | null {
  const link = providerLinks.links.find((item) => item.role === "ads" && item.provider === provider)
  return link ? providerAccountDisplayName(link.account) : null
}

function cockpitSiteContextArea(
  site: SiteRecord,
  crawlState: SiteCrawlState | null,
  onOpenTab: (tab: SiteTab) => void,
  onRecrawl: () => void,
  recrawling: boolean
): CockpitArea {
  const pageCount = crawlState?.pages.length ?? 0
  const indexed = crawlState?.intelligence?.status === "ready" || pageCount > 0
  const crawling = isCrawlActive(crawlState?.latestJob ?? null)
  const summary = `${site.primaryDomain ?? "Site"}${pageCount ? ` · ${pageCount} pages indexed` : ""}`
  const viewPages: CockpitAction = {
    label: "View pages",
    onClick: () => onOpenTab("seo"),
    variant: "light",
  }
  if (crawling) {
    return {
      key: "site",
      label: "Site context",
      tone: "progress",
      statusLabel: "Crawling",
      summary,
      recommendation: "Reading your site to generate copy and pick landing pages.",
      primary: viewPages,
    }
  }
  if (indexed) {
    return {
      key: "site",
      label: "Site context",
      tone: "ready",
      statusLabel: "Crawled",
      summary,
      recommendation: null,
      primary: viewPages,
    }
  }
  return {
    key: "site",
    label: "Site context",
    tone: "attention",
    statusLabel: "Not crawled",
    summary: site.primaryDomain ?? "Site",
    recommendation: "Crawl the site so Adgiro can generate copy and pick landing pages.",
    primary: {
      label: recrawling ? "Crawling…" : "Crawl site",
      onClick: onRecrawl,
    },
  }
}

function cockpitGoogleArea(
  site: SiteRecord,
  generation: CampaignGenerationState | null,
  providerLinks: SiteProviderLinksPayload,
  onOpenTab: (tab: SiteTab) => void,
  onConnectProviders: () => void
): CockpitArea {
  const rollups = site.summary?.channels
  const states = [rollups?.googleSearch, rollups?.googlePmax].filter(
    (state): state is SiteChannelRollupState => Boolean(state)
  )
  const accountName = linkedAdsAccountName(providerLinks, "google")
  const readiness = bestChannelReadiness(generation, ["google_search", "google_pmax"])
  const summary = accountName ? `Ad account: ${accountName}` : "No Google Ads account linked"
  const base = { key: "google", label: "Google Ads" }

  if (states.includes("failed")) {
    return {
      ...base,
      tone: "attention",
      statusLabel: "Needs attention",
      summary,
      recommendation: "A campaign failed to sync. Open campaigns to fix it.",
      primary: { label: "Review", onClick: () => onOpenTab("campaigns") },
    }
  }
  if (states.includes("live")) {
    return {
      ...base,
      tone: "live",
      statusLabel: "Live",
      summary,
      recommendation: null,
      primary: { label: "View campaigns", onClick: () => onOpenTab("campaigns"), variant: "light" },
    }
  }
  if (states.includes("paused")) {
    return {
      ...base,
      tone: "idle",
      statusLabel: "Paused",
      summary,
      recommendation: null,
      primary: { label: "View campaigns", onClick: () => onOpenTab("campaigns"), variant: "light" },
    }
  }
  if (states.includes("needs_sync") || states.includes("draft")) {
    return {
      ...base,
      tone: "ready",
      statusLabel: "Ready to launch",
      summary,
      recommendation: "Campaigns are built and ready to go live.",
      primary: { label: "Review & launch", onClick: () => onOpenTab("campaigns") },
    }
  }
  if (!accountName) {
    return {
      ...base,
      tone: "idle",
      statusLabel: "Not connected",
      summary: "No Google Ads account linked",
      recommendation: "Connect Google to launch a Search campaign.",
      primary: { label: "Connect Google", onClick: onConnectProviders },
    }
  }
  if (readiness.ready) {
    return {
      ...base,
      tone: "ready",
      statusLabel: "Ready to launch",
      summary,
      recommendation: "Everything's in place — build and launch your Search campaign.",
      primary: { label: "Review & launch", onClick: () => onOpenTab("campaigns") },
    }
  }
  return {
    ...base,
    tone: "progress",
    statusLabel: "Finishing setup",
    summary,
    recommendation: readiness.topMissing ?? "A few assets are still being prepared.",
    primary: { label: "Finish setup", onClick: () => onOpenTab("campaigns") },
  }
}

function cockpitMetaArea(
  site: SiteRecord,
  generation: CampaignGenerationState | null,
  providerLinks: SiteProviderLinksPayload,
  onOpenTab: (tab: SiteTab) => void,
  onConnectProviders: () => void
): CockpitArea {
  const rollup = site.summary?.channels.metaAds
  const metaLink = providerLinks.links.find(
    (item) => item.role === "ads" && item.provider === "meta"
  )
  const accountName = metaLink ? providerAccountDisplayName(metaLink.account) : null
  const pageId = metaLink ? metaSelectedPageId(metaLink.account.metadata) : ""
  const pageLabel = metaLink ? metaPageSetupLabel(metaLink.account.metadata) : null
  const readiness = bestChannelReadiness(generation, ["meta_ads"])
  const summary = accountName
    ? `${accountName}${pageLabel ? ` · ${pageLabel}` : ""}`
    : "Add Facebook & Instagram ads after connecting Meta"
  const base = { key: "meta", label: "Meta Ads" }

  if (!metaLink) {
    return {
      ...base,
      tone: "idle",
      statusLabel: "Not connected",
      summary: "Add Facebook & Instagram ads after connecting Meta",
      recommendation: null,
      primary: { label: "Connect Meta", onClick: onConnectProviders },
    }
  }
  if (!pageId) {
    return {
      ...base,
      tone: "progress",
      statusLabel: "Choose a Page",
      summary,
      recommendation: "Select the Facebook Page these ads run from.",
      primary: { label: "Select Page", onClick: () => onOpenTab("setup") },
    }
  }
  if (rollup === "failed") {
    return {
      ...base,
      tone: "attention",
      statusLabel: "Needs attention",
      summary,
      recommendation: "A campaign failed to sync. Open campaigns to fix it.",
      primary: { label: "Review", onClick: () => onOpenTab("campaigns") },
    }
  }
  if (rollup === "live") {
    return {
      ...base,
      tone: "live",
      statusLabel: "Live",
      summary,
      recommendation: null,
      primary: { label: "View campaigns", onClick: () => onOpenTab("campaigns"), variant: "light" },
    }
  }
  if (rollup === "needs_sync" || rollup === "draft" || readiness.ready) {
    return {
      ...base,
      tone: "ready",
      statusLabel: "Ready to add",
      summary,
      recommendation: "Meta assets are matched and ready to launch.",
      primary: { label: "Review & launch", onClick: () => onOpenTab("campaigns") },
    }
  }
  return {
    ...base,
    tone: "progress",
    statusLabel: "Finishing setup",
    summary,
    recommendation: readiness.topMissing ?? "A few assets are still being prepared.",
    primary: { label: "Finish setup", onClick: () => onOpenTab("campaigns") },
  }
}

function cockpitTrackingArea(
  tracking: SiteTrackingPayload | null,
  onOpenTab: (tab: SiteTab) => void
): CockpitArea {
  const measurement = tracking?.config.ga4MeasurementId ?? null
  const activeMappings =
    tracking?.mappings.filter((mapping) => mapping.status === "active").length ?? 0
  const tagSeen = (tracking?.observedPatterns.length ?? 0) > 0
  const base = { key: "tracking", label: "Tracking" }

  if (!measurement) {
    return {
      ...base,
      tone: "attention",
      statusLabel: "Needs setup",
      summary: "Conversion tracking not configured",
      recommendation: "Set up GA4 measurement so campaigns can optimize for conversions.",
      primary: { label: "Set up tracking", onClick: () => onOpenTab("setup") },
    }
  }
  if (activeMappings === 0) {
    return {
      ...base,
      tone: "progress",
      statusLabel: "Map conversions",
      summary: `${measurement} · ${tagSeen ? "receiving events" : "no events yet"}`,
      recommendation: "Map the events that count as conversions.",
      primary: { label: "Map conversions", onClick: () => onOpenTab("tracking") },
    }
  }
  return {
    ...base,
    tone: "ready",
    statusLabel: "Conversions mapped",
    summary: `${activeMappings} mapped · ${measurement}`,
    recommendation: null,
    primary: { label: "View tracking", onClick: () => onOpenTab("tracking"), variant: "light" },
  }
}

function SiteCockpitPanel({
  site,
  generation,
  loading,
  providerLinks,
  tracking,
  crawlState,
  recrawling,
  onOpenTab,
  onConnectProviders,
  onRecrawl,
}: {
  site: SiteRecord
  generation: CampaignGenerationState | null
  loading: boolean
  providerLinks: SiteProviderLinksPayload
  tracking: SiteTrackingPayload | null
  crawlState: SiteCrawlState | null
  recrawling: boolean
  onOpenTab: (tab: SiteTab) => void
  onConnectProviders: () => void
  onRecrawl: () => void
}) {
  const areas: CockpitArea[] = [
    cockpitSiteContextArea(site, crawlState, onOpenTab, onRecrawl, recrawling),
    cockpitGoogleArea(site, generation, providerLinks, onOpenTab, onConnectProviders),
    cockpitMetaArea(site, generation, providerLinks, onOpenTab, onConnectProviders),
    cockpitTrackingArea(tracking, onOpenTab),
  ]

  const summary = site.summary
  const nextStep =
    areas.find((area) => area.tone === "attention") ??
    areas.find((area) => area.tone === "progress") ??
    null
  const allHandled = areas.every((area) => area.tone === "live" || area.tone === "ready")
  const headline = loading
    ? "Checking your setup…"
    : nextStep
      ? `Next: ${nextStep.label} — ${nextStep.recommendation ?? nextStep.statusLabel.toLowerCase()}`
      : allHandled
        ? "You're all set. Campaigns are ready or running."
        : "Review the areas below to get your campaigns live."

  const metrics = summary
    ? [
        { label: "Live campaigns", value: String(summary.liveCampaignCount) },
        { label: "Spend (30d)", value: cockpitMoney(summary.spendMicros30Days) },
        { label: "Conversions (30d)", value: String(summary.conversions30Days) },
      ]
    : []

  return (
    <AppCard className="site-cockpit-card">
      <section className="site-cockpit-header">
        <div className="site-cockpit-headline">
          <Text component="h2" fw={700} size="lg">
            Launch readiness
          </Text>
          <Text c="dimmed" size="sm">
            {goalLabel(site.primaryGoal)} · {site.primaryDomain ?? "no domain"}
          </Text>
          <p className="site-cockpit-next">{headline}</p>
        </div>
        {metrics.length > 0 ? (
          <div className="site-cockpit-metrics">
            {metrics.map((metric) => (
              <div className="site-cockpit-metric" key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {loading ? (
        <div className="site-cockpit-rows">
          <EmptyState>Checking channel readiness…</EmptyState>
        </div>
      ) : (
        <div className="site-cockpit-rows">
          {areas.map((area) => (
            <div className={`site-cockpit-row site-cockpit-row--${area.tone}`} key={area.key}>
              <div className="site-cockpit-row__main">
                <div className="site-cockpit-row__label">
                  <strong>{area.label}</strong>
                  <StatusBadge color={cockpitToneColor(area.tone)}>{area.statusLabel}</StatusBadge>
                </div>
                <span className="site-cockpit-row__summary">{area.summary}</span>
                {area.recommendation ? (
                  <span className="site-cockpit-row__hint">{area.recommendation}</span>
                ) : null}
              </div>
              <Button
                size="sm"
                variant={area.primary.variant ?? "filled"}
                onClick={area.primary.onClick}
              >
                {area.primary.label}
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="site-cockpit-footnote">
        Need the detailed controls? Every area opens its full settings in the tabs above.
      </p>
    </AppCard>
  )
}

const siteProviderRoles: SiteProviderRole[] = ["ads", "analytics", "search_console"]

const siteTabs: Array<{ id: SiteTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "segments", label: "Segments" },
  { id: "setup", label: "Setup" },
  { id: "tracking", label: "Tracking" },
  { id: "campaigns", label: "Campaigns" },
  { id: "seo", label: "SEO" },
  { id: "analytics", label: "Analytics" },
]

function siteDetailUrlState(search: string): {
  tab: SiteTab
  segmentId: string | null
  assetTab: AssetTab
  campaignChannelId: string | null
} {
  const params = new URLSearchParams(search)
  const rawTab = params.get("tab")
  const segmentId = params.get("segment")
  // A bare segment deep-link (no explicit tab) opens the Segments workspace, not the overview.
  const tab = rawTab ? toSiteTab(rawTab) : segmentId ? "segments" : "overview"
  return {
    tab,
    segmentId,
    assetTab: toAssetTab(params.get("assetTab")),
    campaignChannelId: params.get("campaignChannel"),
  }
}

function toSiteTab(value: string | null): SiteTab {
  return siteTabs.some((tab) => tab.id === value) ? (value as SiteTab) : "overview"
}

function toAssetTab(value: string | null): AssetTab {
  return assetTabs.some((tab) => tab.id === value) ? (value as AssetTab) : "overview"
}

const assetTypes: SiteAssetType[] = [
  "landing_page",
  "location",
  "audience",
  "competitor",
  "search_intent",
  "keyword_theme",
  "negative_keyword",
  "headline",
  "description",
  "offer",
  "proof_point",
  "site_link",
  "image",
  "video",
]

const targetingAssetTypes = [
  "location",
  "audience",
  "competitor",
  "search_intent",
  "keyword_theme",
  "negative_keyword",
] satisfies SiteAssetType[]

const copyAssetTypes = [
  "headline",
  "description",
  "offer",
  "proof_point",
  "site_link",
] satisfies SiteAssetType[]

const assetTabs: Array<{ id: AssetTab; label: string; types: SiteAssetType[] | null }> = [
  { id: "overview", label: "Overview", types: null },
  { id: "landing", label: "Landing", types: ["landing_page"] },
  {
    id: "targeting",
    label: "Targeting",
    types: [
      "location",
      "audience",
      "competitor",
      "search_intent",
      "keyword_theme",
      "negative_keyword",
    ],
  },
  {
    id: "copy",
    label: "Copy",
    types: ["headline", "description", "offer", "proof_point", "site_link"],
  },
  { id: "media", label: "Media", types: ["image", "video"] },
]

const mediaAddMethods: Array<{ id: MediaAddMethod; label: string }> = [
  { id: "library", label: "Library" },
  { id: "upload", label: "Upload" },
  { id: "url", label: "URL" },
]

const overviewCategories: Array<{
  id: Exclude<AssetTab, "overview">
  label: string
  types: SiteAssetType[]
}> = [
  { id: "landing", label: "Landing", types: ["landing_page"] },
  {
    id: "targeting",
    label: "Targeting",
    types: [
      "location",
      "audience",
      "competitor",
      "search_intent",
      "keyword_theme",
      "negative_keyword",
    ],
  },
  {
    id: "copy",
    label: "Copy",
    types: ["headline", "description", "offer", "proof_point", "site_link"],
  },
  { id: "media", label: "Media", types: ["image", "video"] },
]

function assetPayload(form: AssetFormState, existingAsset?: SiteAssetRecord | null) {
  const name = isTextCopyAssetType(form.assetType) ? form.value : form.name
  const common = {
    assetType: form.assetType,
    name: name || undefined,
    role: form.assetType,
  }
  if (
    existingAsset &&
    (existingAsset.assetType === "image" || existingAsset.assetType === "video") &&
    existingAsset.valueJson
  ) {
    return {
      ...common,
      valueUrl: existingAsset.valueUrl,
      valueJson: existingAsset.valueJson,
      source: existingAsset.source,
    }
  }
  if (["image", "video", "landing_page", "site_link"].includes(form.assetType)) {
    return { ...common, valueUrl: form.value }
  }
  return { ...common, valueText: form.value }
}

function imageGroupAssetPayload(asset: SiteAssetRecord, title: string) {
  const valueJson = imageGroupValueJson(asset, title)
  return {
    assetType: asset.assetType,
    name: imageGroupAssetName(asset, title),
    role: asset.segmentRole ?? asset.assetType,
    valueUrl: asset.valueUrl,
    valueText: asset.valueText,
    valueJson,
    source: asset.source,
  }
}

function imageGroupValueJson(
  asset: SiteAssetRecord,
  title: string
): Record<string, unknown> | null {
  if (!asset.valueJson) return null
  const next = { ...asset.valueJson }
  if (typeof next.conceptTitle === "string") next.conceptTitle = title
  if (typeof next.title === "string") next.title = title
  if (typeof next.originalName === "string") next.originalName = imageGroupAssetName(asset, title)
  if (typeof next.rationale === "string") delete next.rationale
  return next
}

function imageGroupAssetName(asset: SiteAssetRecord, title: string): string {
  const variant = generatedImageInfo(asset)?.variant
  const suffix = variant ? ` (${imageVariantLabel(variant)})` : assetNameVariantSuffix(asset.name)
  const derivedSuffix = /\(Google PMax 1\.91:1\)\s*$/i.test(asset.name)
    ? " (Google PMax 1.91:1)"
    : ""
  return `${title}${suffix}${derivedSuffix}`.trim()
}

function assetNameVariantSuffix(name: string): string {
  const match = name.match(
    /\s+\((Square|Vertical|Horizontal)\)(?:\s+\(Google PMax 1\.91:1\))?\s*$/i
  )
  return match ? ` (${match[1]})` : ""
}

function assetFormFromRecord(asset: SiteAssetRecord): AssetFormState {
  return {
    assetType: asset.assetType,
    name: asset.name ?? "",
    value: asset.valueText ?? asset.valueUrl ?? "",
  }
}

function assetTypeLabel(type: SiteAssetType): string {
  switch (type) {
    case "landing_page":
      return "Landing page"
    case "search_intent":
      return "Search intent"
    case "competitor":
      return "Competitor"
    case "keyword_theme":
      return "Keyword theme"
    case "negative_keyword":
      return "Excluded search term"
    case "proof_point":
      return "Proof point"
    case "site_link":
      return "Site link"
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}

function isTextCopyAssetType(type: SiteAssetType): boolean {
  return type === "headline" || type === "description" || type === "offer" || type === "proof_point"
}

function isTargetingAssetType(value: string): value is (typeof targetingAssetTypes)[number] {
  return targetingAssetTypes.includes(value as (typeof targetingAssetTypes)[number])
}

function isCopyAssetType(value: string): value is (typeof copyAssetTypes)[number] {
  return copyAssetTypes.includes(value as (typeof copyAssetTypes)[number])
}

function generateAssetTypeLabel(
  type: (typeof targetingAssetTypes)[number] | (typeof copyAssetTypes)[number]
): string {
  switch (type) {
    case "location":
      return "locations"
    case "audience":
      return "audiences"
    case "competitor":
      return "competitors"
    case "search_intent":
    case "keyword_theme":
      return "search intents"
    case "negative_keyword":
      return "excluded search terms"
    case "headline":
      return "headlines"
    case "description":
      return "descriptions"
    case "offer":
      return "offers"
    case "proof_point":
      return "proof points"
    case "site_link":
      return "site links"
  }
}

function assetPlaceholder(type: SiteAssetType): string {
  switch (type) {
    case "landing_page":
      return "/tampa"
    case "location":
      return "Tampa, FL"
    case "audience":
      return "Restaurant owners"
    case "competitor":
      return "HubSpot"
    case "search_intent":
    case "keyword_theme":
      return "restaurant marketing tampa"
    case "negative_keyword":
      return "jobs, pay fine, free"
    case "headline":
      return "Grow faster in Tampa"
    case "description":
      return "Launch ads for your Tampa audience."
    case "offer":
      return "Free consultation"
    case "proof_point":
      return "Trusted by local teams"
    case "site_link":
      return "https://example.com/pricing"
    case "image":
      return "https://example.com/image.jpg"
    case "video":
      return "https://example.com/video.mp4"
  }
}

function assetDisplayValue(asset: SiteAssetRecord): string {
  if ((asset.assetType === "image" || asset.assetType === "video") && asset.valueJson) {
    const width = typeof asset.valueJson.width === "number" ? asset.valueJson.width : null
    const height = typeof asset.valueJson.height === "number" ? asset.valueJson.height : null
    const variant = typeof asset.valueJson.variant === "string" ? asset.valueJson.variant : null
    const originalName =
      typeof asset.valueJson.originalName === "string" ? asset.valueJson.originalName : null
    const mimeType = typeof asset.valueJson.mimeType === "string" ? asset.valueJson.mimeType : null
    const dimensions = width && height ? `${String(width)}x${String(height)}` : null
    return [variant, dimensions, originalName, mimeType].filter(Boolean).join(" · ") || asset.source
  }
  if ((asset.assetType === "image" || asset.assetType === "video") && asset.valueUrl) {
    return asset.valueUrl.startsWith("http") ? asset.valueUrl : asset.source
  }
  if (asset.assetType === "site_link" && asset.valueUrl) {
    const path = asset.valueJson?.path
    return typeof path === "string" && path ? path : asset.valueUrl
  }
  return asset.valueText ?? asset.valueUrl ?? asset.source
}

function assetDistinctName(asset: SiteAssetRecord): string | null {
  if (asset.assetType === "image" || asset.assetType === "video") return null
  const name = normalizeAssetDisplayText(asset.name)
  if (!name) return null
  if (asset.assetType === "site_link") {
    return name !== normalizeAssetDisplayText(asset.valueText) ? asset.name : null
  }
  return name !== normalizeAssetDisplayText(assetDisplayValue(asset)) ? asset.name : null
}

function normalizeAssetDisplayText(value: string | null | undefined): string {
  return value?.trim().toLowerCase().replace(/\s+/g, " ") ?? ""
}

function assetRowClassName(
  asset: SiteAssetRecord,
  resolution: LocationResolutionRecord | null
): string | undefined {
  const classes = []
  if (generatedImageInfo(asset)) classes.push("asset-variant-row")
  if (resolution?.status === "failed" || resolution?.status === "ambiguous") {
    classes.push("asset-row--needs-attention")
  }
  return classes.length > 0 ? classes.join(" ") : undefined
}

function locationResolution(asset: SiteAssetRecord): LocationResolutionRecord | null {
  if (asset.assetType !== "location") return null
  const resolution = asset.valueJson?.locationResolution
  if (!resolution || typeof resolution !== "object") return null
  const candidate = resolution as Partial<LocationResolutionRecord>
  return candidate.kind === "location" && typeof candidate.status === "string"
    ? (candidate as LocationResolutionRecord)
    : null
}

function locationHiddenProviderLocationKeys(asset: SiteAssetRecord): Set<string> {
  const keys = asset.valueJson?.locationHiddenProviderLocationKeys
  return new Set(
    Array.isArray(keys) ? keys.filter((key): key is string => typeof key === "string") : []
  )
}

function providerLocationKey(location: ProviderLocationMatchRecord): string | null {
  return (
    stringRecordValue(location.id) ??
    stringRecordValue(location.key) ??
    stringRecordValue(location.resourceName)
  )
}

function providerLocationDismissKey(provider: string, locationKey: string): string {
  return `${provider}:${locationKey}`
}

function providerLocationLabel(location: ProviderLocationMatchRecord): string {
  const name =
    stringRecordValue(location.canonicalName) ?? stringRecordValue(location.name) ?? "Location"
  const type = stringRecordValue(location.targetType) ?? stringRecordValue(location.type)
  return type ? `${name} (${type})` : name
}

function stringRecordValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function locationResolutionLabel(status: LocationResolutionRecord["status"]): string {
  switch (status) {
    case "resolved":
      return "Resolved"
    case "partial":
      return "Partial"
    case "ambiguous":
      return "Needs detail"
    case "failed":
      return "Failed"
  }
}

function locationProviderStatusLabel(
  status: LocationResolutionRecord["providers"][string]["status"]
): string {
  switch (status) {
    case "not_linked":
      return "Not linked"
    case "resolved":
      return "Resolved"
    case "failed":
      return "Failed"
  }
}

function providerLabel(provider: string): string {
  if (provider === "google_ads") return "Google Ads"
  if (provider === "meta_ads") return "Meta Ads"
  return provider
}

function conversionValidationProviderLabel(provider: string): string {
  if (provider === "adgiro") return "Adgiro received"
  if (provider === "google_ads") return "Google Ads"
  if (provider === "meta") return "Meta"
  return provider
}

function conversionValidationStatusLabel(
  status: ConversionValidationResultStatus | "queued" | "running" | "succeeded" | "failed"
): string {
  if (status === "not_configured") return "Not configured"
  if (status === "pending_attribution") return "Pending attribution"
  if (status === "queued") return "Queued"
  if (status === "running") return "Running"
  if (status === "succeeded") return "Initialized"
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function conversionValidationStatusColor(
  status: ConversionValidationResultStatus | "queued" | "running" | "succeeded" | "failed"
): "green" | "yellow" | "red" | "gray" | "blue" {
  if (status === "passed" || status === "succeeded") return "green"
  if (status === "warning" || status === "pending_attribution") return "yellow"
  if (status === "failed") return "red"
  if (status === "waiting" || status === "running" || status === "queued") return "blue"
  return "gray"
}

function imageVariantLabel(variant: string): string {
  switch (variant) {
    case "square":
      return "Square"
    case "vertical":
      return "Vertical"
    case "horizontal":
      return "Horizontal"
    default:
      return variant
  }
}

function candidateOptionLabel(candidate: SiteProviderLinksPayload["candidates"][number]): string {
  const accountLabel = providerAccountDisplayName(candidate)
  const accountNumber =
    accountLabel === candidate.externalAccountId ? "" : ` - ${candidate.externalAccountId}`
  const matchLabel =
    candidate.matchReasons.length > 0 ? ` - ${candidate.matchReasons.join(", ")}` : ""
  return `${accountLabel} (${labelForProvider(candidate.provider)} ${formatAccountType(
    candidate.accountType
  )})${accountNumber}${matchLabel}`
}

function metaPageSetupLabel(metadata: Record<string, unknown> | null): string {
  const pageName = typeof metadata?.pageName === "string" ? metadata.pageName : null
  if (pageName) return `Page: ${pageName}`
  const pages = metadata?.pages
  if (Array.isArray(pages) && pages.length > 0) return "Page: needs selection"
  return "Page: reconnect Meta with Page access"
}

function metaSelectedPageId(metadata: Record<string, unknown> | null): string {
  return typeof metadata?.pageId === "string" ? metadata.pageId : ""
}

function metaPageOptions(metadata: Record<string, unknown> | null): Array<{
  value: string
  label: string
}> {
  const pages = metadata?.pages
  if (!Array.isArray(pages)) return []
  return pages.flatMap((page) => {
    if (!page || typeof page !== "object") return []
    const record = page as Record<string, unknown>
    const id = typeof record.id === "string" ? record.id : null
    const name = typeof record.name === "string" ? record.name : null
    return id && name ? [{ value: id, label: name }] : []
  })
}

function providerAccountDisplayName(account: {
  label: string | null
  externalAccountId: string
}): string {
  const label = account.label?.trim()
  return label && label !== account.externalAccountId ? label : account.externalAccountId
}
